import { useState, useMemo, useEffect, useRef, useCallback, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { useAppData } from "@/data/AppDataContext";
import { useJobsQuery } from "@/hooks/useJobsQuery";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory, type JobAllocation } from "@/data/WarehouseInventoryContext";
import { useProductRegistry, type RegistryProduct } from "@/data/ProductRegistryContext";
import { VendorPOService } from "@/services/vendor.service";
import { WarehouseService } from "@/services/warehouse.service";
import { JobService } from "@/services/job.service";
import * as ProductRegistryService from "@/services/productRegistry.service";
import { canEnterStage } from "@/data/workflowEngine";
import { getJobBom, getJobBomSourceLabel, type JobBomSource } from "@/data/jobBomSelector";
import { OrderingPacketsLane } from "@/components/ordering/OrderingPacketsLane";
import { WarehouseSelector } from "@/components/ordering/WarehouseSelector";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { ORDERING_QUEUE_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import {
  useOrderingQueueJobsPage,
  useOrderingQueueRealtimeRefreshSignal,
  useOrderingQueueSummary,
} from "@/hooks/useOrderingQueueServerPaging";
import type { OrderingQueueVendorGroup } from "@/services/ordering-queue-page.service";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitFilterBar, CockpitSearch,
} from "@/components/cockpit/CockpitShell";
import { formatShortDate } from "@/lib/dateFormat";
import {
  Search, Package, ChevronDown, ChevronRight, Copy, Download, Mail, CheckCircle2,
  ClipboardList, Truck, Clock, AlertTriangle, ShoppingCart, Zap, LayoutList, Rows3,
  ClipboardCheck, Send, Plus, Save, Trash2, RotateCcw, Pencil,
} from "lucide-react";
import { ReportFrictionButton } from "@/components/ReportFrictionButton";
import { ActionRequiredPanel, type ActionRequiredItem } from "@/components/ActionRequiredPanel";
import { PermissionGate } from "@/components/PermissionGate";
import { getUnmatchedSkus } from "@/lib/skuValidation";
import { UnmatchedSkuBanner } from "@/components/UnmatchedSkuBanner";
import type { Job } from "@/data/types";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { isInRescission, getRescissionDaysRemaining } from "@/data/rescission";
import { migrationBypasses } from "@/data/import/migrationVisibilityGuard";
import type { TrackingStatusTag, POStatus, PurchaseOrder, POTrackingEntry, POTrackingPurpose } from "@/data/VendorPOContext";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { DraftSpecDriftPanel } from "@/components/jobspec/DraftSpecDriftPanel";
import type { OrderingPacket } from "@/data/orderingPacket/types";
import { useWindowedList } from "@/hooks/useWindowedList";
import { useRole } from "@/data/RoleContext";
import { useStandaloneOrdersPage } from "@/hooks/useStandaloneOrdersPage";
import { PlaceOrderModal } from "@/components/ordering/PlaceOrderModal";
import type { StandaloneOrderWithPOs } from "@/services/standalone-order.service";
import { POAttachments } from "@/components/ordering/POAttachments";

/* ═══════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════ */

type VendorGroup = OrderingQueueVendorGroup;

type QueueFilter = "all" | "not-ordered" | "ordered" | "confirmed" | "delivered";
type EnrichedOrderingJob = {
  job: Job;
  vendorGroups: VendorGroup[];
  bomSource: JobBomSource;
  pos: PurchaseOrder[];
};

type MarkOrderedDialogState = {
  job: Job;
  vg: VendorGroup;
};

type MarkDeliveredDialogState = {
  job: Job;
  vg: VendorGroup;
  vendorGroups?: VendorGroup[];
};

/* ═══════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════ */

function getProductLabel(job: Job): string {
  const pc = job.projectConfig;
  const isUuidLike = (value: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
  const clean = (value: unknown): string => {
    const v = String(value ?? "").trim();
    if (!v) return "";
    if (isUuidLike(v)) return "";
    return v;
  };

  if (!pc) return clean(job.projectType) || "Project";
  const mat = clean(pc.primaryMaterial) || clean(pc.selectedWallSystem);
  const sys = clean(pc.bathroomSystemType) || clean(pc.autoProjectType) || clean(job.projectType);
  if (mat && sys && mat !== sys) return `${mat} ${sys}`;
  return mat || sys || "Project";
}

function mapPacketPoStatusToQueueStatus(status: string): POStatus | "Not Ordered" {
  switch (status) {
    case "sent":
      return "Submitted";
    case "acknowledged":
    case "partially_received":
    case "invoiced":
    case "paid":
    case "disputed":
      return "Confirmed";
    case "fully_received":
    case "closed":
      return "Delivered";
    default:
      return "Not Ordered";
  }
}

function inferVendorFromSkuOrName(skuValue: string, nameValue: string): string {
  const sku = String(skuValue || "").toUpperCase();
  const name = String(nameValue || "").toLowerCase();
  if (sku.includes("SEN")) return "Sentrel";
  if (name.includes("dekton")) return "Cosentino";
  if (name.includes("tile") || name.includes("daltile")) return "Daltile";
  if (
    name.includes("fixture") ||
    name.includes("grab") ||
    name.includes("rod") ||
    name.includes("drain") ||
    name.includes("adhesive") ||
    name.includes("silicone")
  ) {
    return "Home Depot";
  }
  return "";
}

function isUuidLikeValue(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );
}

function sanitizeProductLabel(skuValue: string, nameValue: string): string {
  const name = String(nameValue || "").trim();
  if (name && !isUuidLikeValue(name)) return name;
  const sku = String(skuValue || "").trim();
  if (sku && !isUuidLikeValue(sku)) return sku;
  return "Unmapped Product";
}

function normalizeMaterialSku(value: unknown): string {
  return String(value ?? "").trim().toUpperCase();
}

function toLocalDateInputValue(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function localDateInputToIso(value: string): string | null {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const parts = normalized.split("-").map((part) => Number(part));
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function isAssignedVendorName(value: unknown): boolean {
  const normalized = String(value ?? "").trim().toLowerCase();
  return Boolean(
    normalized &&
    normalized !== "unassigned" &&
    normalized !== "unknown" &&
    normalized !== "auto-mapped" &&
    normalized !== "n/a",
  );
}

function resolveCanonicalVendorName(
  value: string,
  vendors: ReturnType<typeof useVendorPO>["vendors"],
): string {
  const normalized = value.trim();
  if (!normalized) return "";
  const master = vendors.find((vendor) => vendor.vendorName.toLowerCase() === normalized.toLowerCase());
  return master?.vendorName || normalized;
}

function resolveVendorNameFromSkuMapping(
  row: ProductRegistryService.ProductVendorSkuMapRow | undefined,
  vendors: ReturnType<typeof useVendorPO>["vendors"],
): string {
  if (!row) return "";
  const vendorById = vendors.find((vendor) => String(vendor.vendorId).toLowerCase() === String(row.vendor_id).toLowerCase());
  const value = vendorById?.vendorName || row.vendor_name || "";
  return isAssignedVendorName(value) ? resolveCanonicalVendorName(String(value), vendors) : "";
}

function addSkuMappingCandidate(
  map: Map<string, ProductRegistryService.ProductVendorSkuMapRow>,
  key: unknown,
  row: ProductRegistryService.ProductVendorSkuMapRow,
) {
  const normalizedKey = normalizeMaterialSku(key);
  if (!normalizedKey) return;
  const current = map.get(normalizedKey);
  const currentConfidence = Number(current?.confidence ?? -1);
  const nextConfidence = Number(row.confidence ?? -1);
  if (!current || nextConfidence > currentConfidence) {
    map.set(normalizedKey, row);
  }
}

function getMaterialVendorHint(material: NonNullable<Job["materialsList"]>[number]): string {
  const row = material as NonNullable<Job["materialsList"]>[number] & {
    vendor?: unknown;
    vendorName?: unknown;
    preferredVendor?: unknown;
    supplier?: unknown;
    sourceVendor?: unknown;
  };
  return String(row.vendor ?? row.vendorName ?? row.preferredVendor ?? row.supplier ?? row.sourceVendor ?? "").trim();
}

function resolveMaterialVendorName(input: {
  sku: unknown;
  productName: unknown;
  rawVendor?: unknown;
  intendedVendor?: unknown;
  vendors: ReturnType<typeof useVendorPO>["vendors"];
  registryBySku: Map<string, RegistryProduct>;
  crosswalkByVendorSku: Map<string, ProductRegistryService.ProductVendorSkuMapRow>;
  crosswalkByInternalSku: Map<string, ProductRegistryService.ProductVendorSkuMapRow>;
}): string {
  const sku = normalizeMaterialSku(input.sku);

  const registryVendor = sku ? input.registryBySku.get(sku)?.vendor : undefined;
  if (isAssignedVendorName(registryVendor)) {
    return resolveCanonicalVendorName(String(registryVendor), input.vendors);
  }

  const vendorSkuVendor = resolveVendorNameFromSkuMapping(
    sku ? input.crosswalkByVendorSku.get(sku) : undefined,
    input.vendors,
  );
  if (vendorSkuVendor) return vendorSkuVendor;

  const internalSkuVendor = resolveVendorNameFromSkuMapping(
    sku ? input.crosswalkByInternalSku.get(sku) : undefined,
    input.vendors,
  );
  if (internalSkuVendor) return internalSkuVendor;

  if (isAssignedVendorName(input.rawVendor)) {
    return resolveCanonicalVendorName(String(input.rawVendor), input.vendors);
  }

  if (isAssignedVendorName(input.intendedVendor)) {
    return resolveCanonicalVendorName(String(input.intendedVendor), input.vendors);
  }

  const inferredVendor = inferVendorFromSkuOrName(String(input.sku ?? ""), String(input.productName ?? ""));
  return inferredVendor ? resolveCanonicalVendorName(inferredVendor, input.vendors) : "Unassigned";
}

function deriveVendorGroups(
  job: Job,
  vendors: ReturnType<typeof useVendorPO>["vendors"],
  existingPOs: PurchaseOrder[],
  jobPackets: OrderingPacket[],
  liveAllocations: JobAllocation[] = [],
  registryProducts: RegistryProduct[] = [],
  vendorSkuMappings: ProductRegistryService.ProductVendorSkuMapRow[] = [],
): VendorGroup[] {
  const pos = existingPOs;
  const materials = job.materialsList || [];
  const registryBySku = new Map(
    registryProducts
      .filter((product) => normalizeMaterialSku(product.sku))
      .map((product) => [normalizeMaterialSku(product.sku), product]),
  );
  const activeVendorSkuMappings = vendorSkuMappings.filter((row) => row.is_active !== false);
  const crosswalkByVendorSku = new Map<string, ProductRegistryService.ProductVendorSkuMapRow>();
  const crosswalkByInternalSku = new Map<string, ProductRegistryService.ProductVendorSkuMapRow>();
  for (const row of activeVendorSkuMappings) {
    addSkuMappingCandidate(crosswalkByVendorSku, row.vendor_sku, row);
    addSkuMappingCandidate(crosswalkByInternalSku, row.internal_sku, row);
  }
  const latestPacket = [...jobPackets].sort((a, b) =>
    String(b?.sentAt ?? "").localeCompare(String(a?.sentAt ?? "")),
  )[0];
  const requiredQtyBySku = new Map<string, number>();
  const allocatedQtyBySku = new Map<string, number>();
  for (const mat of materials) {
    const sku = normalizeMaterialSku(mat.sku);
    if (!sku) continue;
    requiredQtyBySku.set(sku, (requiredQtyBySku.get(sku) ?? 0) + Math.max(0, Number(mat.quantityRequired ?? 0)));
    allocatedQtyBySku.set(sku, (allocatedQtyBySku.get(sku) ?? 0) + Math.max(0, Number(mat.quantityAllocated ?? 0)));
  }
  const liveAllocatedBySku = new Map<string, number>();
  for (const allocation of liveAllocations) {
    if (allocation.jobId !== job.id) continue;
    if (allocation.status !== "Reserved" && allocation.status !== "Ready for Install") continue;
    const sku = normalizeMaterialSku(allocation.sku);
    if (!sku) continue;
    liveAllocatedBySku.set(sku, (liveAllocatedBySku.get(sku) ?? 0) + Math.max(0, Number(allocation.quantity ?? 0)));
  }
  for (const [sku, liveAllocated] of liveAllocatedBySku.entries()) {
    allocatedQtyBySku.set(sku, Math.max(allocatedQtyBySku.get(sku) ?? 0, liveAllocated));
  }
  const pendingOrderableBySku = new Map<string, number>();
  for (const [sku, required] of requiredQtyBySku.entries()) {
    const allocated = allocatedQtyBySku.get(sku) ?? 0;
    pendingOrderableBySku.set(sku, Math.max(0, required - allocated));
  }

  const grouped: Record<string, VendorGroup> = {};

  // Prefer packet lines, but key sections by PO (not vendor). Any line without PO stays
  // in a pending vendor bucket until a PO is generated.
  if (latestPacket && Array.isArray(latestPacket.lineItems) && latestPacket.lineItems.length > 0) {
    const packetPosById = new Map((latestPacket.purchaseOrders ?? []).map((po) => [po.id, po]));
    const dbPosById = new Map(pos.map((po) => [po.poId, po]));
    const orderedQtyBySkuFromPacket = new Map<string, number>();

    for (const line of latestPacket.lineItems) {
      if (line.status === "cancelled") continue;
      const rawVendor = String(line.actualVendor ?? line.projectedVendor ?? "").trim();
      const intendedVendor = String(latestPacket.intendedVendor ?? "").trim();
      const vName = resolveMaterialVendorName({
        sku: line.actualSku ?? line.projectedSku,
        productName: line.actualItemName ?? line.projectedItemName,
        rawVendor,
        intendedVendor,
        vendors,
        registryBySku,
        crosswalkByVendorSku,
        crosswalkByInternalSku,
      });
      const linkedDbPO =
        (line.poId ? dbPosById.get(line.poId) : undefined)
        ?? pos.find((po) => po.vendorName === vName);
      const packetPO =
        (line.poId ? packetPosById.get(line.poId) : undefined)
        ?? (latestPacket.purchaseOrders ?? []).find((po) => po.vendorName === vName);
      const poId = linkedDbPO?.poId ?? packetPO?.id;
      if (!poId) continue;
      const sku = normalizeMaterialSku(line.actualSku ?? line.projectedSku);
      if (!sku) continue;
      const qty = Math.max(0, Number(line.actualQty ?? line.projectedQty ?? 0));
      if (qty <= 0) continue;
      orderedQtyBySkuFromPacket.set(sku, (orderedQtyBySkuFromPacket.get(sku) ?? 0) + qty);
    }
    for (const [sku, orderedQty] of orderedQtyBySkuFromPacket.entries()) {
      const remainingOrderable = pendingOrderableBySku.get(sku);
      if (remainingOrderable === undefined) continue;
      pendingOrderableBySku.set(sku, Math.max(0, remainingOrderable - orderedQty));
    }

    for (const line of latestPacket.lineItems) {
      if (line.status === "cancelled") continue;
      const lineSku = normalizeMaterialSku(line.actualSku ?? line.projectedSku);
      const rawVendor = String(line.actualVendor ?? line.projectedVendor ?? "").trim();
      const intendedVendor = String(latestPacket.intendedVendor ?? "").trim();
      const vName = resolveMaterialVendorName({
        sku: line.actualSku ?? line.projectedSku,
        productName: line.actualItemName ?? line.projectedItemName,
        rawVendor,
        intendedVendor,
        vendors,
        registryBySku,
        crosswalkByVendorSku,
        crosswalkByInternalSku,
      });
      const vendor = vendors.find((v) => v.vendorName === vName);

      const linkedDbPO =
        (line.poId ? dbPosById.get(line.poId) : undefined)
        ?? pos.find((po) => po.vendorName === vName);
      const packetPO =
        (line.poId ? packetPosById.get(line.poId) : undefined)
        ?? (latestPacket.purchaseOrders ?? []).find((po) => po.vendorName === vName);

      const poId = linkedDbPO?.poId ?? packetPO?.id;
      const isPendingLine = !poId;
      const originalQuantity = Math.max(0, Number(line.actualQty ?? line.projectedQty ?? 1));
      let quantity = originalQuantity;
      if (isPendingLine && lineSku) {
        const remainingOrderable = pendingOrderableBySku.get(lineSku);
        if (remainingOrderable !== undefined) {
          const clampedQty = Math.max(0, Math.min(quantity, remainingOrderable));
          pendingOrderableBySku.set(lineSku, Math.max(0, remainingOrderable - clampedQty));
          quantity = clampedQty;
        }
      }
      if (isPendingLine && quantity <= 0) continue;

      const poNumber = linkedDbPO?.poNumber ?? packetPO?.poNumber;
      const poStatus = linkedDbPO?.status ?? (packetPO ? mapPacketPoStatusToQueueStatus(packetPO.status) : "Not Ordered");
      const groupKey = poId ? `po:${poId}` : `pending:${vName}`;

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          vendorName: vName,
          vendorId: linkedDbPO?.vendorId || vendor?.vendorId || "",
          vendorEmail: vendor?.email || "",
          orderingMethod: vendor?.orderingMethod || "Manual",
          items: [],
          totalCost: 0,
          status: poStatus,
          poId,
          poNumber,
          submittedAt: linkedDbPO?.submittedAt,
          deliveredAt: linkedDbPO?.deliveredAt,
          externalPoNumber: linkedDbPO?.externalPoNumber,
          trackingNumbers: linkedDbPO?.trackingNumbers ?? [],
        };
      }

      const unitCost = line.actualUnitPrice ?? line.projectedUnitPrice ?? 0;
      const displayName = sanitizeProductLabel(
        String(line.actualSku ?? line.projectedSku ?? ""),
        String(line.actualItemName ?? line.projectedItemName ?? line.projectedSku ?? ""),
      );
      grouped[groupKey].items.push({
        sku: line.actualSku ?? line.projectedSku ?? "—",
        productName: displayName,
        quantity,
        unitCost,
      });
      grouped[groupKey].totalCost += quantity === originalQuantity && line.actualLineCost != null
        ? line.actualLineCost
        : unitCost * quantity;
    }
  } else {
    // Legacy path:
    // 1) If DB POs already exist, render by PO sections directly.
    // 2) Otherwise, build pending vendor sections for not-yet-ordered lines.
    if (pos.length > 0) {
      const orderedQtyBySku = new Map<string, number>();
      for (const po of pos) {
        const vendor = vendors.find((v) => v.vendorName === po.vendorName);
        const groupKey = `po:${po.poId}`;
        grouped[groupKey] = {
          vendorName: po.vendorName,
          vendorId: po.vendorId || vendor?.vendorId || "",
          vendorEmail: vendor?.email || "",
          orderingMethod: vendor?.orderingMethod || "Manual",
          items: po.items.map((item) => ({
            sku: item.sku || "—",
            productName: item.productName,
            quantity: item.quantity,
            unitCost: item.unitCost,
          })),
          totalCost: po.totalCost,
          status: po.status,
          poId: po.poId,
          poNumber: po.poNumber,
          submittedAt: po.submittedAt,
          deliveredAt: po.deliveredAt,
          externalPoNumber: po.externalPoNumber,
          trackingNumbers: po.trackingNumbers ?? [],
        };
        for (const item of po.items) {
          const sku = normalizeMaterialSku(item.sku);
          if (!sku) continue;
          orderedQtyBySku.set(sku, (orderedQtyBySku.get(sku) ?? 0) + Math.max(0, Number(item.quantity ?? 0)));
        }
      }

      // Include still-missing material lines even when some POs already exist.
      for (const mat of materials) {
        const sku = normalizeMaterialSku(mat.sku);
        if (!sku) continue;
        const requiredQty = Math.max(0, Number(mat.quantityRequired ?? 1));
        if (requiredQty <= 0) continue;
        const orderedQty = orderedQtyBySku.get(sku) ?? 0;
        const allocatedQty = allocatedQtyBySku.get(sku) ?? 0;
        const coveredQty = Math.min(requiredQty, orderedQty + allocatedQty);
        const remainingQty = Math.max(0, requiredQty - coveredQty);
        if (remainingQty <= 0) continue;

        const rawVendor = getMaterialVendorHint(mat);
        const vName = resolveMaterialVendorName({
          sku: mat.sku,
          productName: mat.itemName,
          rawVendor,
          intendedVendor: job.selectedVendor,
          vendors,
          registryBySku,
          crosswalkByVendorSku,
          crosswalkByInternalSku,
        });
        const vendor = vendors.find((v) => v.vendorName === vName);
        const groupKey = `pending:${vName}`;
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            vendorName: vName,
            vendorId: vendor?.vendorId || "",
            vendorEmail: vendor?.email || "",
            orderingMethod: vendor?.orderingMethod || "Manual",
            items: [],
            totalCost: 0,
            status: "Not Ordered",
            poId: undefined,
            trackingNumbers: [],
          };
        }
        const unitCost = mat.unitCost || 0;
        const displayName = sanitizeProductLabel(String(mat.sku ?? ""), String(mat.itemName ?? ""));
        grouped[groupKey].items.push({
          sku: mat.sku || "—",
          productName: displayName,
          quantity: remainingQty,
          unitCost,
        });
        grouped[groupKey].totalCost += unitCost * remainingQty;
      }
    } else {
      for (const mat of materials) {
        const sku = normalizeMaterialSku(mat.sku);
        if (sku) {
          const remainingOrderable = pendingOrderableBySku.get(sku);
          if (remainingOrderable !== undefined && remainingOrderable <= 0) continue;
        }
        const rawVendor = getMaterialVendorHint(mat);
        const vName = resolveMaterialVendorName({
          sku: mat.sku,
          productName: mat.itemName,
          rawVendor,
          intendedVendor: job.selectedVendor,
          vendors,
          registryBySku,
          crosswalkByVendorSku,
          crosswalkByInternalSku,
        });
        const vendor = vendors.find(v => v.vendorName === vName);
        const groupKey = `pending:${vName}`;
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            vendorName: vName,
            vendorId: vendor?.vendorId || "",
            vendorEmail: vendor?.email || "",
            orderingMethod: vendor?.orderingMethod || "Manual",
            items: [],
            totalCost: 0,
            status: "Not Ordered",
            poId: undefined,
            trackingNumbers: [],
          };
        }
        const unitCost = mat.unitCost || 0;
        const displayName = sanitizeProductLabel(String(mat.sku ?? ""), String(mat.itemName ?? ""));
        grouped[groupKey].items.push({
          sku: mat.sku || "—",
          productName: displayName,
          quantity: sku ? (pendingOrderableBySku.get(sku) ?? 0) : (mat.quantityRequired || 1),
          unitCost,
        });
        grouped[groupKey].totalCost += unitCost * (sku ? (pendingOrderableBySku.get(sku) ?? 0) : (mat.quantityRequired || 1));
        if (sku) pendingOrderableBySku.set(sku, 0);
      }
    }
  }

  return Object.values(grouped);
}

const STATUS_COLORS: Record<string, string> = {
  "Not Ordered": "bg-muted text-muted-foreground",
  "Draft": "bg-muted text-muted-foreground",
  "Submitted": "bg-info/15 text-info dark:bg-info/30 dark:text-info",
  "Confirmed": "bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning",
  "In Transit": "bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning",
  "Delivered": "bg-success/15 text-success dark:bg-success/30 dark:text-success",
  "Received": "bg-success/15 text-success dark:bg-success/30 dark:text-success",
};

function getPOStatusLabel(status: POStatus | "Not Ordered"): string {
  if (status === "Submitted") return "Ordered";
  if (status === "In Transit") return "In Transit";
  if (status === "Received") return "Received";
  return status;
}

const TRACKING_PURPOSE_LABELS: Record<POTrackingPurpose, string> = {
  inbound: "Inbound",
  outbound: "Outbound",
  return: "Return",
  reship: "Reship",
  replacement: "Replacement",
  other: "Other",
};

const TRACKING_PURPOSE_OPTIONS = Object.entries(TRACKING_PURPOSE_LABELS) as Array<[POTrackingPurpose, string]>;

const TRACKING_STATUS_OPTIONS: Array<[TrackingStatusTag | "", string]> = [
  ["", "— Status"],
  ["Pending", "Pending"],
  ["InfoReceived", "Info Received"],
  ["InTransit", "In Transit"],
  ["OutForDelivery", "Out for Delivery"],
  ["Delivered", "Delivered"],
  ["Exception", "Exception"],
  ["Expired", "Expired"],
];

const CARRIER_OPTIONS: Array<[string, string]> = [
  ["", "— Carrier"],
  ["TForce Freight", "TForce Freight"],
  ["UPS", "UPS"],
  ["ABF Freight", "ABF Freight"],
  ["Other", "Other"],
];

/** Auto-detect carrier from tracking number pattern. */
function inferCarrier(trackingNumber: string): string {
  const tn = trackingNumber.trim().toUpperCase();
  if (tn.startsWith("1Z")) return "UPS";
  return "";
}

/** Resolve the carrier-specific tracking URL. */
function resolveTrackingUrl(carrier: string | undefined, trackingNumber: string): string {
  switch ((carrier ?? "").trim()) {
    case "UPS":
      return `https://www.ups.com/track?loc=en_US&requester=ST/&tracknum=${encodeURIComponent(trackingNumber)}`;
    case "ABF Freight":
      return "https://arcb.com/shippers/solutions/less-than-truckload/abf-freight";
    case "TForce Freight":
    default:
      return "https://www.tforcefreight.com/ltl/apps/home/";
  }
}

function trackingStatusTone(tag: TrackingStatusTag | undefined): string {
  if (!tag) return "text-muted-foreground";
  if (tag === "Delivered") return "text-emerald-600 dark:text-emerald-400";
  if (tag === "InTransit" || tag === "OutForDelivery") return "text-amber-600 dark:text-amber-400";
  if (tag === "Exception" || tag === "Expired") return "text-red-600 dark:text-red-400";
  return "text-muted-foreground";
}

function createBlankTrackingEntry(): POTrackingEntry {
  return {
    id: `trk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    trackingNumber: "",
    carrier: "",
    purpose: "outbound",
    notes: "",
    createdAt: new Date().toISOString(),
  };
}

function normalizeTrackingDraft(entries: POTrackingEntry[]): POTrackingEntry[] {
  return entries
    .map((entry) => ({
      ...entry,
      trackingNumber: String(entry.trackingNumber || "").trim(),
      carrier: String(entry.carrier || "").trim() || undefined,
      purpose: entry.purpose || "outbound",
      notes: String(entry.notes || "").trim() || undefined,
      createdAt: entry.createdAt || new Date().toISOString(),
    }))
    .filter((entry) => entry.trackingNumber.length > 0);
}

const CAN_UPLOAD_PROOFS = new Set(["super_admin", "admin", "owner", "ops_manager", "procurement_manager", "warehouse_manager"]);

function POFulfillmentPanel({
  vg,
  disabled,
  onSave,
}: {
  vg: VendorGroup;
  disabled: boolean;
  onSave: (poId: string, input: { externalPoNumber: string; trackingNumbers: POTrackingEntry[] }) => Promise<void>;
}) {
  const { accountRole } = useRole();
  const canUpload = CAN_UPLOAD_PROOFS.has(accountRole ?? "");
  const hasPersistedData =
    Boolean(vg.externalPoNumber?.trim()) || (vg.trackingNumbers ?? []).length > 0;

  const [isEditing, setIsEditing] = useState(!hasPersistedData);
  const [externalPoNumber, setExternalPoNumber] = useState(vg.externalPoNumber ?? "");
  const [trackingNumbers, setTrackingNumbers] = useState<POTrackingEntry[]>(vg.trackingNumbers ?? []);
  const [saving, setSaving] = useState(false);
  const persistedTrackingKey = JSON.stringify(normalizeTrackingDraft(vg.trackingNumbers ?? []));

  useEffect(() => {
    if (isEditing) return; // don't overwrite in-progress edits on server refresh
    setExternalPoNumber(vg.externalPoNumber ?? "");
    setTrackingNumbers(vg.trackingNumbers ?? []);
  }, [vg.poId, vg.externalPoNumber, persistedTrackingKey, isEditing]);

  if (!vg.poId) return null;

  const persistedExternal = String(vg.externalPoNumber ?? "").trim();
  const persistedTracking = normalizeTrackingDraft(vg.trackingNumbers ?? []);
  const draftTracking = normalizeTrackingDraft(trackingNumbers);
  const isDirty =
    externalPoNumber.trim() !== persistedExternal ||
    JSON.stringify(draftTracking) !== JSON.stringify(persistedTracking);

  const updateTracking = (id: string, patch: Partial<POTrackingEntry>) => {
    setTrackingNumbers((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  };

  const handleCancel = () => {
    setExternalPoNumber(vg.externalPoNumber ?? "");
    setTrackingNumbers(vg.trackingNumbers ?? []);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(vg.poId!, {
        externalPoNumber,
        trackingNumbers: draftTracking,
      });
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  /* ── Read-only view ── */
  if (!isEditing) {
    const savedTracking = normalizeTrackingDraft(vg.trackingNumbers ?? []);
    return (
      <div className="mt-3 rounded-lg border border-border/80 bg-muted/10 p-3 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              External fulfillment
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            disabled={disabled}
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-3 w-3" />
            Edit
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-[minmax(220px,320px)_1fr]">
          <div className="space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              External PO / vendor ref
            </p>
            <p className="text-xs text-foreground">
              {vg.externalPoNumber?.trim() || (
                <span className="text-muted-foreground">None</span>
              )}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Tracking numbers
            </p>
            {savedTracking.length === 0 ? (
              <p className="text-xs text-muted-foreground">None</p>
            ) : (
              <div className="space-y-1">
                {savedTracking.map((entry) => (
                  <div key={entry.id} className="flex flex-wrap gap-1.5 items-center">
                    <Badge variant="outline" className="text-[10px]">
                      {entry.purpose ?? "outbound"}
                    </Badge>
                    {entry.carrier && (
                      <span className="text-xs text-muted-foreground">{entry.carrier}</span>
                    )}
                    <div className="space-y-0.5 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono">{entry.trackingNumber}</span>
                        {entry.trackingStatus && (
                          <span className={`text-[10px] font-medium ${trackingStatusTone(entry.trackingStatus)}`}>
                            {entry.trackingStatus}
                          </span>
                        )}
                        <a
                          href={resolveTrackingUrl(entry.carrier, entry.trackingNumber)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5"
                        >
                          Track ↗
                        </a>
                      </div>
                      {entry.lastEventDescription && (
                        <p className="text-[10px] text-muted-foreground">
                          {entry.lastEventDescription}
                          {entry.lastEventAt && (
                            <span className="ml-1 opacity-60">
                              · {new Date(entry.lastEventAt).toLocaleDateString()}
                            </span>
                          )}
                        </p>
                      )}
                      {entry.estimatedDelivery && (
                        <p className="text-[10px] text-muted-foreground">
                          ETA: {new Date(entry.estimatedDelivery).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </p>
                      )}
                    </div>
                    {entry.notes && (
                      <span className="text-xs text-muted-foreground">— {entry.notes}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="pt-2 border-t">
          <POAttachments poId={vg.poId} canUpload={canUpload} />
        </div>
      </div>
    );
  }

  /* ── Edit view ── */
  return (
    <div className="mt-3 rounded-lg border border-border/80 bg-muted/10 p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            External fulfillment
          </p>
          <p className="text-[11px] text-muted-foreground">
            Vendor PO/reference plus all shipment, return, or reship tracking numbers.
          </p>
        </div>
        {draftTracking.length > 0 && (
          <Badge variant="outline" className="text-[10px] shrink-0">
            {draftTracking.length} tracking
          </Badge>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(220px,320px)_1fr]">
        <label className="space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            External PO / vendor ref
          </span>
          <Input
            value={externalPoNumber}
            onChange={(event) => setExternalPoNumber(event.target.value)}
            placeholder="Vendor PO, SO, or order #"
            className="h-8 text-xs"
            disabled={disabled || saving}
          />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Tracking numbers
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              disabled={disabled || saving}
              onClick={() => setTrackingNumbers((prev) => [...prev, createBlankTrackingEntry()])}
            >
              <Plus className="h-3 w-3" />
              Add tracking
            </Button>
          </div>

          {trackingNumbers.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-3 py-2 text-[11px] text-muted-foreground">
              No tracking numbers recorded yet.
            </div>
          ) : (
            <div className="space-y-2">
              {trackingNumbers.map((entry) => (
                <div key={entry.id} className="grid gap-2 md:grid-cols-[110px_110px_1fr_120px_1fr_auto]">
                  <select
                    value={entry.purpose ?? "outbound"}
                    onChange={(event) => updateTracking(entry.id, { purpose: event.target.value as POTrackingPurpose })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                    disabled={disabled || saving}
                  >
                    {TRACKING_PURPOSE_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <select
                    value={entry.carrier ?? ""}
                    onChange={(event) => updateTracking(entry.id, { carrier: event.target.value })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                    disabled={disabled || saving}
                  >
                    {CARRIER_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <Input
                    value={entry.trackingNumber}
                    onChange={(event) => {
                      const tn = event.target.value;
                      const updates: Partial<POTrackingEntry> = { trackingNumber: tn };
                      if (!entry.carrier) {
                        const detected = inferCarrier(tn);
                        if (detected) updates.carrier = detected;
                      }
                      updateTracking(entry.id, updates);
                    }}
                    placeholder="Tracking / PRO number"
                    className="h-8 text-xs"
                    disabled={disabled || saving}
                  />
                  <select
                    value={entry.trackingStatus ?? ""}
                    onChange={(event) => updateTracking(entry.id, { trackingStatus: (event.target.value || undefined) as TrackingStatusTag | undefined })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                    disabled={disabled || saving}
                  >
                    {TRACKING_STATUS_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <Input
                    value={entry.notes ?? ""}
                    onChange={(event) => updateTracking(entry.id, { notes: event.target.value })}
                    placeholder="Notes"
                    className="h-8 text-xs"
                    disabled={disabled || saving}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    disabled={disabled || saving}
                    onClick={() => setTrackingNumbers((prev) => prev.filter((row) => row.id !== entry.id))}
                    aria-label="Remove tracking number"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        {hasPersistedData && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            disabled={saving}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          disabled={disabled || saving || !isDirty}
          onClick={() => { void handleSave(); }}
        >
          <Save className="h-3 w-3" />
          {saving ? "Saving..." : "Save fulfillment"}
        </Button>
      </div>
      <div className="pt-2 border-t">
        <POAttachments poId={vg.poId} canUpload={canUpload} />
      </div>
    </div>
  );
}

/* ── Standalone (Direct) Order Row ── */
function StandaloneOrderRow({ order, onReload }: { order: StandaloneOrderWithPOs; onReload: () => void }) {
  const [expanded, setExpanded] = useState(false);

  const allDelivered =
    order.pos.length > 0 && order.pos.every((po) => po.status === "Delivered");
  const allReceived =
    order.pos.length > 0 && order.pos.every((po) => po.status === "Received");
  const displayStatus: string = allReceived || allDelivered ? "Delivered" : "Submitted";

  const totalCost = order.pos.reduce((sum, po) => sum + po.totalCost, 0);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="text-[9px] bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30">
              Direct Order
            </Badge>
            <span className="text-sm font-semibold">{order.refNumber}</span>
            {order.description && (
              <span className="text-xs text-muted-foreground truncate">{order.description}</span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {order.pos.length} vendor{order.pos.length !== 1 ? "s" : ""} ·{" "}
            ${totalCost.toLocaleString()} · {formatShortDate(order.createdAt)}
          </p>
        </div>
        <Badge
          variant="outline"
          className={`text-[10px] shrink-0 ${STATUS_COLORS[displayStatus] ?? ""}`}
        >
          {getPOStatusLabel(displayStatus as POStatus)}
        </Badge>
      </button>

      {expanded && (
        <div className="border-t bg-muted/10 p-3 space-y-2">
          {order.pos.map((po) => (
            <div key={po.poId} className="rounded border p-2 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium">{po.vendorName}</span>
                <Badge variant="outline" className="text-[9px] font-mono">
                  {po.poNumber}
                </Badge>
                {po.externalPoNumber && (
                  <Badge variant="outline" className="text-[9px] font-mono">
                    {po.externalPoNumber}
                  </Badge>
                )}
                <Badge className={`text-[9px] ${STATUS_COLORS[po.status] ?? ""}`}>
                  {getPOStatusLabel(po.status as POStatus)}
                </Badge>
              </div>
              {po.items.map((item) => (
                <div
                  key={item.sku}
                  className="flex items-center gap-2 text-[11px] text-muted-foreground pl-2"
                >
                  <span className="font-mono w-24 shrink-0 truncate">{item.sku}</span>
                  <span className="flex-1 truncate">{item.productName}</span>
                  <span className="tabular-nums">×{item.quantity}</span>
                  <span className="tabular-nums">
                    ${(item.quantity * item.unitCost).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground shrink-0">Warehouse:</span>
                <div className="flex-1 max-w-[200px]">
                  <WarehouseSelector
                    value={po.warehouseId ?? null}
                    onChange={async (warehouseId) => {
                      try {
                        await VendorPOService.updateWarehouseAssignment(po.poId, warehouseId);
                        onReload();
                      } catch {
                        toast({ title: "Failed to update warehouse", variant: "destructive" });
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════ */

export default function OrderingQueue() {
  const serverPagingEnabled = ORDERING_QUEUE_SERVER_PAGING_ENABLED;
  const realtimeRefreshToken = useOrderingQueueRealtimeRefreshSignal(serverPagingEnabled);
  const [manualRefreshToken, setManualRefreshToken] = useState(0);
  const orderingRefreshToken = realtimeRefreshToken + manualRefreshToken;
  const refreshOrderingServerPages = useCallback(() => {
    if (serverPagingEnabled) setManualRefreshToken((current) => current + 1);
  }, [serverPagingEnabled]);
  const { jobs, setJobs, refresh: refreshJobs } = useJobsQuery({ view: "all", search: "" });
  const { refreshData: refreshAppData, lastRefreshedAt } = useAppData();
  const { vendors, purchaseOrders, setPurchaseOrders, getPOsByJob, refreshData: refreshVendorPo } = useVendorPO();
  const { shipments, receivingRecords, jobAllocations, setShipments, setReceivingRecords, refreshData: refreshWarehouse } = useWarehouseInventory();
  const { products: registryProducts } = useProductRegistry();
  const [orderingSkuProducts, setOrderingSkuProducts] = useState<RegistryProduct[]>([]);
  const [orderingSkuProductsLoading, setOrderingSkuProductsLoading] = useState(false);
  const [orderingVendorSkuMappings, setOrderingVendorSkuMappings] = useState<ProductRegistryService.ProductVendorSkuMapRow[]>([]);
  const [orderingVendorSkuMappingsLoading, setOrderingVendorSkuMappingsLoading] = useState(false);
  const { drafts } = useOrderDraft();
  const { accountRole } = useRole();
  const { packets } = useOrderingPacket();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<QueueFilter>("all");
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [detailJob, setDetailJob] = useState<Job | null>(null);
  const [repairingAllocations, setRepairingAllocations] = useState(false);
  const [poTaxOverrides, setPoTaxOverrides] = useState<Record<string, number>>({});
  const poActionInFlightRef = useRef<Set<string>>(new Set());
  const [poActionInFlightKeys, setPoActionInFlightKeys] = useState<Set<string>>(new Set());
  const canRunAllocationRepair = accountRole === "super_admin" || accountRole === "admin" || accountRole === "ops_manager" || accountRole === "it_staff";
  const orderingSummary = useOrderingQueueSummary({
    enabled: serverPagingEnabled,
    refreshToken: orderingRefreshToken,
  });

  const beginPoAction = useCallback((lockKey: string): boolean => {
    if (poActionInFlightRef.current.has(lockKey)) return false;
    poActionInFlightRef.current.add(lockKey);
    setPoActionInFlightKeys((prev) => {
      const next = new Set(prev);
      next.add(lockKey);
      return next;
    });
    return true;
  }, []);

  const endPoAction = useCallback((lockKey: string) => {
    poActionInFlightRef.current.delete(lockKey);
    setPoActionInFlightKeys((prev) => {
      if (!prev.has(lockKey)) return prev;
      const next = new Set(prev);
      next.delete(lockKey);
      return next;
    });
  }, []);

  const isPoActionInFlight = useCallback(
    (lockKey: string) => poActionInFlightKeys.has(lockKey),
    [poActionInFlightKeys],
  );

  // Top-level lane: Packets (manual vendor ordering, the new system) vs Jobs (legacy PO/job-grouped view)
  const [topLane, setTopLane] = useState<"packets" | "jobs">(() => {
    if (typeof window === "undefined") return "packets";
    return (localStorage.getItem("orderingQueue.topLane") as "packets" | "jobs") || "packets";
  });
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("orderingQueue.topLane", topLane);
  }, [topLane]);

  const activePacketCount = serverPagingEnabled
    ? orderingSummary.data?.activePackets ?? 0
    : packets.filter((p) => !["received", "cancelled"].includes(p.status)).length;
  const exceptionPacketCount = serverPagingEnabled
    ? orderingSummary.data?.packetExceptions ?? 0
    : packets.filter((p) => p.exceptionCount > 0).length;

  // View mode — user preference persisted in localStorage.
  // "single" = legacy unified queue · "sections" = Ready to Prep / Prepared / Pushed lanes.
  const [viewMode, setViewMode] = useState<"single" | "sections">(() => {
    if (typeof window === "undefined") return "single";
    return (localStorage.getItem("orderingQueue.viewMode") as "single" | "sections") || "single";
  });
  const [focusedJobModalId, setFocusedJobModalId] = useState<string | null>(null);
  const [markOrderedDialog, setMarkOrderedDialog] = useState<MarkOrderedDialogState | null>(null);
  const [markOrderedDate, setMarkOrderedDate] = useState<string>(() => toLocalDateInputValue(new Date().toISOString()));
  const [markOrderedWarehouseId, setMarkOrderedWarehouseId] = useState<string | null>(null);
  const [markDeliveredDialog, setMarkDeliveredDialog] = useState<MarkDeliveredDialogState | null>(null);
  const [markDeliveredDate, setMarkDeliveredDate] = useState<string>(() => toLocalDateInputValue(new Date().toISOString()));
  const [reverseOrderDialog, setReverseOrderDialog] = useState<{ job: Job; vg: VendorGroup } | null>(null);
  const [showPlaceOrderModal, setShowPlaceOrderModal] = useState(false);
  const { orders: standaloneOrders, reload: reloadStandaloneOrders } =
    useStandaloneOrdersPage(orderingRefreshToken);
  // undefined entry = all items selected (default). Explicit Set = user has made a selection.
  const [selectedMaterials, setSelectedMaterials] = useState<Record<string, Set<string>>>({});
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("orderingQueue.viewMode", viewMode);
  }, [viewMode]);

  const [serverJobsPage, setServerJobsPage] = useState(0);
  const [serverJobsPageSize, setServerJobsPageSize] = useState(20);
  const serverJobs = useOrderingQueueJobsPage({
    enabled: serverPagingEnabled && topLane === "jobs",
    filter,
    search,
    page: serverJobsPage,
    pageSize: serverJobsPageSize,
    refreshToken: orderingRefreshToken,
  });

  useEffect(() => {
    if (serverPagingEnabled) return;
    const refreshedAt = lastRefreshedAt ? Date.parse(lastRefreshedAt) : 0;
    const shouldRefreshAppData = !refreshedAt || Number.isNaN(refreshedAt) || (Date.now() - refreshedAt) > 60_000;
    const shouldRefreshVendorPo = vendors.length === 0 && purchaseOrders.length === 0;
    const shouldRefreshWarehouse =
      (shipments.length === 0 && receivingRecords.length === 0) ||
      jobAllocations.length === 0;

    const tasks: Array<Promise<void>> = [];
    if (shouldRefreshAppData) tasks.push(refreshAppData());
    if (shouldRefreshVendorPo) tasks.push(refreshVendorPo());
    if (shouldRefreshWarehouse) tasks.push(refreshWarehouse());
    if (tasks.length > 0) void Promise.all(tasks);
  }, [
    refreshAppData,
    refreshVendorPo,
    refreshWarehouse,
    lastRefreshedAt,
    vendors.length,
    purchaseOrders.length,
    shipments.length,
    receivingRecords.length,
    jobAllocations.length,
    serverPagingEnabled,
  ]);

  useEffect(() => {
    const availableRegistryProducts = registryProducts.filter((product) => product.active && normalizeMaterialSku(product.sku));
    if (availableRegistryProducts.length > 0) {
      setOrderingSkuProducts(availableRegistryProducts);
      setOrderingSkuProductsLoading(false);
      return;
    }

    if (orderingSkuProducts.length > 0 || orderingSkuProductsLoading) return;
    let active = true;
    setOrderingSkuProductsLoading(true);
    void ProductRegistryService.getProductsForSkuMatching()
      .then((products) => {
        if (!active) return;
        setOrderingSkuProducts(products.filter((product) => product.active && normalizeMaterialSku(product.sku)));
      })
      .catch((error) => {
        console.error("[OrderingQueue] Failed to load Product Registry SKU vendor data:", error);
      })
      .finally(() => {
        if (active) setOrderingSkuProductsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderingSkuProducts.length, registryProducts]);

  // Resolved product list for SKU matching — uses the fresh fetch when context is deferred.
  const resolvedRegistryProducts = useMemo(
    () => orderingSkuProducts.length > 0
      ? orderingSkuProducts
      : registryProducts.filter((p) => p.active && normalizeMaterialSku(p.sku)),
    [orderingSkuProducts, registryProducts],
  );

  useEffect(() => {
    if (orderingVendorSkuMappings.length > 0 || orderingVendorSkuMappingsLoading) return;
    let active = true;
    setOrderingVendorSkuMappingsLoading(true);
    void ProductRegistryService.getVendorSkuMappings()
      .then((rows) => {
        if (!active) return;
        setOrderingVendorSkuMappings(rows.filter((row) => row.is_active !== false));
      })
      .catch((error) => {
        console.error("[OrderingQueue] Failed to load vendor SKU crosswalk data:", error);
      })
      .finally(() => {
        if (active) setOrderingVendorSkuMappingsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderingVendorSkuMappings.length]);

  // Jobs that qualify for ordering queue
  // Jobs that qualify for ordering queue — gated by workflow engine
  const queueJobs = useMemo(() => {
    if (serverPagingEnabled) return [];
    const hasMaterialGap = (job: Job): boolean => {
      const rows = Array.isArray(job.materialsList) ? job.materialsList : [];
      const rowGap = rows.some((row) => Number(row.missingItems ?? 0) > 0);
      if (rowGap) return true;
      if (String(job.missingItemsList ?? "").trim().length > 0) return true;
      const readiness = String(job.materialReadiness ?? "").trim();
      return !["Fully Received", "Staged for Job", "Loaded for Crew"].includes(readiness);
    };

    return jobs.filter(j => {
      const buildReviewApproved = (
        migrationBypasses(j, "build_review") ||
        j.approvedForOrdering === true ||
        j.intakeApproved === true ||
        j.scopeLocked === true ||
        j.currentStage === "Review Complete" ||
        // Compat: jobs that reached any ordering-related planning/stage state
        // have clearly passed build review, even if the explicit flags weren't set.
        j.planningStatus === "Ready for Ordering" ||
        j.planningStatus === "Sent to Ordering" ||
        j.planningStatus === "Ordered" ||
        j.planningStatus === "Awaiting Delivery" ||
        j.planningStatus === "Receiving" ||
        j.currentStage === "Ready for Ordering" ||
        j.currentStage === "Ordered" ||
        j.currentStage === "Permit and Order Pending"
      );
      // Hard gate: never show jobs in Ordering Queue before build review approval.
      if (!buildReviewApproved) return false;

      // Use centralized workflow engine to check ordering eligibility
      const orderingGate = canEnterStage(j, "Ready for Ordering");
      if (!orderingGate.met) {
        // Compat fallback: include jobs that are already in ordering-related
        // planning/order states even if the strict gate field was not backfilled.
        const alreadyInOrdering = (
          j.currentStage === "Ready for Ordering" ||
          j.currentStage === "Ordered" ||
          j.currentStage === "Permit and Order Pending" ||
          j.planningStatus === "Ready for Ordering" ||
          j.planningStatus === "Sent to Ordering" ||
          j.planningStatus === "Ordered" ||
          j.planningStatus === "Awaiting Delivery" ||
          j.planningStatus === "Receiving" ||
          j.orderStatus === "Not Ordered" ||
          j.orderStatus === "Submitted" ||
          j.orderStatus === "Confirmed" ||
          j.orderStatus === "Delivered"
        );
        if (!alreadyInOrdering) return false;
      }
      // Exclude jobs already past ordering UNLESS they still have material gaps.
      if (j.planningStatus === "Ready for Install" || j.planningStatus === "Staged") {
        if (!hasMaterialGap(j) && j.orderStatus !== "Delivered") return false;
      }
      return true;
    });
  }, [jobs, serverPagingEnabled]);

  // Enrich with vendor groups
  const enrichedJobs = useMemo(() => {
    const productRows = orderingSkuProducts.length > 0
      ? orderingSkuProducts
      : registryProducts.filter((product) => product.active && normalizeMaterialSku(product.sku));
    if (serverPagingEnabled) {
      return serverJobs.jobs.map((entry) => ({
        ...entry,
        vendorGroups: deriveVendorGroups(
          entry.job,
          vendors,
          entry.pos,
          packets.filter((packet) => packet.jobId === entry.job.id),
          jobAllocations,
          productRows,
          orderingVendorSkuMappings,
        ),
      }));
    }
    return queueJobs.map(job => ({
      job,
      vendorGroups: deriveVendorGroups(
        job,
        vendors,
        getPOsByJob(job.id) as PurchaseOrder[],
        packets.filter((packet) => packet.jobId === job.id),
        jobAllocations,
        productRows,
        orderingVendorSkuMappings,
      ),
      bomSource: getJobBom(job).source as JobBomSource,
      pos: getPOsByJob(job.id),
    }));
  }, [
    getPOsByJob,
    jobAllocations,
    orderingSkuProducts,
    orderingVendorSkuMappings,
    packets,
    purchaseOrders,
    queueJobs,
    registryProducts,
    serverJobs.jobs,
    serverPagingEnabled,
    vendors,
  ]);

  // Filter
  const filteredJobs = useMemo(() => {
    if (serverPagingEnabled) return enrichedJobs;
    let result = enrichedJobs;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(e =>
        String(e.job.customerName ?? "").toLowerCase().includes(s) ||
        String(e.job.id ?? "").toLowerCase().includes(s) ||
        getProductLabel(e.job).toLowerCase().includes(s)
      );
    }
    if (filter !== "all") {
      result = result.filter(e => {
        if (filter === "not-ordered") return e.vendorGroups.some(vg => vg.status === "Not Ordered");
        if (filter === "ordered") return e.vendorGroups.some(vg => vg.status === "Submitted");
        if (filter === "confirmed") return e.vendorGroups.some(vg => vg.status === "Confirmed" || vg.status === "In Transit");
        if (filter === "delivered") return e.vendorGroups.every(vg => vg.status === "Delivered" || vg.status === "Received");
        return true;
      });
    }
    return result;
  }, [enrichedJobs, filter, search, serverPagingEnabled]);
  const clientSinglePagination = usePagination(serverPagingEnabled ? [] : filteredJobs, 20);
  const serverTotalPages = Math.max(1, Math.ceil(serverJobs.count / serverJobsPageSize));
  const serverSafePage = Math.min(serverJobsPage, serverTotalPages - 1);
  const serverSinglePagination = {
    paged: filteredJobs,
    page: serverSafePage,
    totalPages: serverTotalPages,
    totalItems: serverJobs.count,
    setPage: setServerJobsPage,
    hasNext: serverSafePage < serverTotalPages - 1,
    hasPrev: serverSafePage > 0,
    next: () => setServerJobsPage((current) => Math.min(current + 1, serverTotalPages - 1)),
    prev: () => setServerJobsPage((current) => Math.max(current - 1, 0)),
    reset: () => setServerJobsPage(0),
    pageStart: serverJobs.count === 0 ? 0 : serverSafePage * serverJobsPageSize + 1,
    pageEnd: Math.min((serverSafePage + 1) * serverJobsPageSize, serverJobs.count),
    pageSize: serverJobsPageSize,
    setPageSize: setServerJobsPageSize,
  };
  const singlePagination = serverPagingEnabled ? serverSinglePagination : clientSinglePagination;
  useEffect(() => {
    if (serverPagingEnabled) setServerJobsPage(0);
    else clientSinglePagination.reset();
    setListScrollTop(0);
  }, [filter, search, serverPagingEnabled, viewMode]);

  // Counts
  const counts = useMemo(() => {
    if (serverPagingEnabled) {
      return {
        all: orderingSummary.data?.jobsInQueue ?? 0,
        notOrdered: orderingSummary.data?.notOrderedJobs ?? 0,
        ordered: orderingSummary.data?.orderedJobs ?? 0,
        confirmed: orderingSummary.data?.confirmedJobs ?? 0,
        delivered: orderingSummary.data?.deliveredJobs ?? 0,
      };
    }
    return {
      all: enrichedJobs.length,
      notOrdered: enrichedJobs.filter(e => e.vendorGroups.some(vg => vg.status === "Not Ordered")).length,
      ordered: enrichedJobs.filter(e => e.vendorGroups.some(vg => vg.status === "Submitted")).length,
      confirmed: enrichedJobs.filter(e => e.vendorGroups.some(vg => vg.status === "Confirmed" || vg.status === "In Transit")).length,
      delivered: enrichedJobs.filter(e => e.vendorGroups.every(vg => vg.status === "Delivered" || vg.status === "Received")).length,
    };
  }, [enrichedJobs, orderingSummary.data, serverPagingEnabled]);

  useEffect(() => {
    const focusJobId = searchParams.get("job");
    if (!focusJobId) return;
    const target = filteredJobs.find((entry) => entry.job.id === focusJobId)
      ?? enrichedJobs.find((entry) => entry.job.id === focusJobId);
    if (!target) return;
    setExpandedJob(target.job.id);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("job");
    setSearchParams(nextParams, { replace: true });
  }, [enrichedJobs, filteredJobs, searchParams, setSearchParams]);

  const listViewportRef = useRef<HTMLDivElement | null>(null);
  const [listScrollTop, setListScrollTop] = useState(0);
  const LIST_ROW_HEIGHT = 74;
  const LIST_VIEWPORT_HEIGHT = 640;

  const singleWindow = useWindowedList(singlePagination.paged, {
    itemHeight: LIST_ROW_HEIGHT,
    viewportHeight: LIST_VIEWPORT_HEIGHT,
    scrollTop: listScrollTop,
    overscan: 8,
  });

  function isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
  }

  const registryNameBySku = useMemo(
    () =>
      new Map(
        registryProducts
          .map((product) => [String(product.sku || "").trim(), String(product.productName || "").trim()] as const)
          .filter(([sku, name]) => sku.length > 0 && name.length > 0),
      ),
    [registryProducts],
  );

  const registryNameByProductId = useMemo(
    () =>
      new Map(
        registryProducts
          .map((product) => [String(product.productId || "").trim(), String(product.productName || "").trim()] as const)
          .filter(([id, name]) => id.length > 0 && name.length > 0),
      ),
    [registryProducts],
  );

  function resolveProductDisplayName(item: { sku: string; productName: string }): string {
    const rawName = String(item.productName || "").trim();
    if (rawName && !isUuidLike(rawName)) return rawName;

    const sku = String(item.sku || "").trim();
    if (sku) {
      const bySku = registryNameBySku.get(sku);
      if (bySku) return bySku;
    }

    if (rawName) {
      const byProductId = registryNameByProductId.get(rawName);
      if (byProductId) return byProductId;
    }

    if (sku && !isUuidLike(sku)) return sku;
    return "Unmapped Product";
  }

  function getJobDisplayRef(job: Job): string {
    const jobNumber = String((job as { jobNumber?: unknown }).jobNumber ?? "").trim();
    if (jobNumber && !isUuidLike(jobNumber)) return jobNumber;
    if (job.id && !isUuidLike(job.id)) return job.id;
    return "JOB-UNASSIGNED";
  }

  function generateDbSafePoNumber(): string {
    const now = new Date();
    const mmdd = String(now.getMonth() + 1).padStart(2, "0") + String(now.getDate()).padStart(2, "0");
    const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `PO-${mmdd}-${rand}`;
  }

  function purchaseOrderFromVendorGroup(job: Job, vg: VendorGroup): PurchaseOrder | null {
    if (!vg.poId) return null;
    return {
      poId: vg.poId,
      poNumber: vg.poNumber ?? "",
      externalPoNumber: vg.externalPoNumber,
      jobId: job.id,
      jobLabel: `${job.customerName} – ${getProductLabel(job)}`,
      vendorId: vg.vendorId,
      vendorName: vg.vendorName,
      items: vg.items.map((item) => ({
        sku: item.sku,
        productName: item.productName,
        quantity: item.quantity,
        unitCost: item.unitCost,
        lineTotal: item.lineTotal ?? item.quantity * item.unitCost,
      })),
      status: vg.status === "Not Ordered" ? "Draft" : vg.status,
      totalCost: vg.totalCost,
      createdAt: new Date().toISOString(),
      trackingNumbers: vg.trackingNumbers ?? [],
      notes: "",
      flagged: false,
    };
  }

  function deliveryStateForGroups(vendorGroups: VendorGroup[] | undefined, current: VendorGroup) {
    if (!vendorGroups || vendorGroups.length === 0) return null;
    const projected = vendorGroups.map((group) =>
      group.poId === current.poId || (!current.poId && group.vendorName === current.vendorName)
        ? { ...group, status: "Delivered" as const }
        : group,
    );
    return {
      allDelivered: projected.every((group) => group.status === "Delivered"),
      anyDelivered: projected.some((group) => group.status === "Delivered"),
    };
  }

  function isJobInRescission(job: Job): boolean {
    if (job.rescissionStatus === "In Rescission") return true;
    return isInRescission(job.rescissionEndsAt);
  }

  async function syncShipmentStatusForPo(
    poId: string,
    status: "Ordered" | "In Transit" | "Delivered",
    deliveredAtOverride?: string,
  ) {
    let linkedShipment = shipments.find((s) => s.poId === poId);
    if (!linkedShipment && serverPagingEnabled) {
      const dbShipments = await WarehouseService.fetchShipments();
      linkedShipment = dbShipments.find((s) => s.poId === poId);
      if (linkedShipment) {
        setShipments((prev) => [linkedShipment!, ...prev.filter((s) => s.id !== linkedShipment!.id)]);
      }
    }
    if (!linkedShipment) return;
    const ok = await WarehouseService.updateShipmentStatus(linkedShipment.id, status, deliveredAtOverride);
    if (!ok) return;
    const nowIso = deliveredAtOverride ?? new Date().toISOString();
    setShipments((prev) =>
      prev.map((shipment) =>
        shipment.id !== linkedShipment.id
          ? shipment
          : {
              ...shipment,
              status,
              deliveredAt: status === "Delivered" ? nowIso : shipment.deliveredAt,
              receivedAt: shipment.receivedAt,
            },
      ),
    );
  }

  async function syncReceivingRecordsForPoDelivery(
    poId: string,
    fallbackPo?: PurchaseOrder | null,
    fallbackJob?: Job | null,
    deliveredAtOverride?: string,
  ) {
    let poReceivingRecords = receivingRecords.filter((record) => record.poId === poId);
    if (poReceivingRecords.length === 0) {
      const linkedPo = purchaseOrders.find((po) => po.poId === poId) ?? fallbackPo ?? null;
      const linkedJob = linkedPo ? jobs.find((row) => row.id === linkedPo.jobId) ?? fallbackJob ?? null : fallbackJob ?? null;
      // Idempotency guard: local cache may be stale or intentionally deferred;
      // pull DB receiving rows first and reuse them instead of creating duplicates.
      const dbReceivingRows = await WarehouseService.fetchReceivingRecords();
      const existingDbRows = dbReceivingRows.filter((row) => row.poId === poId);
      if (existingDbRows.length > 0) {
        setReceivingRecords((prev) => [
          ...existingDbRows,
          ...prev.filter((r) => !existingDbRows.some((er) => er.id === r.id)),
        ]);
        poReceivingRecords = existingDbRows;
      }
      if (linkedPo && linkedJob && poReceivingRecords.length === 0) {
        const backfilled = await Promise.all(
          linkedPo.items.map((item) =>
            WarehouseService.createReceivingRecord({
              poId: linkedPo.poId,
              poNumber: linkedPo.poNumber,
              vendorName: linkedPo.vendorName,
              jobId: linkedJob.id,
              jobLabel: `${linkedJob.customerName} – ${getProductLabel(linkedJob)}`,
              sku: item.sku,
              productName: item.productName,
              expectedQuantity: item.quantity,
              receivedQuantity: 0,
              status: "Pending",
            }),
          ),
        );
        const createdRows = backfilled.filter((row): row is NonNullable<typeof row> => !!row);
        if (createdRows.length > 0) {
          setReceivingRecords((prev) => [...createdRows, ...prev]);
          poReceivingRecords = createdRows;
        }
      }
    }
    if (poReceivingRecords.length === 0) return;
    const nowIso = deliveredAtOverride ?? new Date().toISOString();
    const updateResults = await Promise.all(
      poReceivingRecords.map(async (record) => {
        const expected = Number(record.expectedQuantity || 0);
        const nextQuantity = expected > 0 ? expected : Number(record.receivedQuantity || 0);
        const ok = await WarehouseService.updateReceivingRecord(record.id, {
          receivedQuantity: nextQuantity,
          status: "Fully Received",
          receivedAt: nowIso,
          receivedBy: "Ordering Queue",
        });
        if (!ok) return null;
        return { id: record.id, receivedQuantity: nextQuantity, status: "Fully Received" as const, receivedAt: nowIso, receivedBy: "Ordering Queue" };
      }),
    );

    const updates = updateResults.filter((row): row is NonNullable<typeof row> => !!row);
    if (updates.length === 0) return;
    setReceivingRecords((prev) =>
      prev.map((record) => {
        const patch = updates.find((u) => u.id === record.id);
        return patch
          ? {
              ...record,
              receivedQuantity: patch.receivedQuantity,
              status: patch.status,
              receivedAt: patch.receivedAt,
              receivedBy: patch.receivedBy,
            }
          : record;
      }),
    );

    // Delivery->Receiving must immediately reconcile allocations so Inventory/Jobs
    // reflects Alloc counts without needing another screen action.
    const affectedJobIds = Array.from(
      new Set(
        poReceivingRecords
          .map((record) => String(record.jobId || "").trim())
          .filter((value) => value.length > 0),
      ),
    );
    await Promise.all(
      affectedJobIds.map(async (jobId) => {
        try {
          await WarehouseService.reconcileJobAllocationsFromReceiving(jobId);
        } catch (error) {
          console.warn("[OrderingQueue] Allocation reconcile failed after delivery:", { jobId, error });
        }
      }),
    );

    // Reconcile writes happen in DB; refresh both warehouse and jobs caches
    // immediately so /inventory and /jobs/:id reflect new allocations now.
    await Promise.all([
      refreshWarehouse(),
      refreshJobs(),
    ]);
  }

  // Mark vendor group as ordered — DB-backed PO + DB-backed shipment/receiving records
  function openMarkOrderedDialog(job: Job, vg: VendorGroup, selectedItems?: VendorGroup["items"]) {
    const filteredItems = selectedItems && selectedItems.length > 0 ? selectedItems : vg.items;
    const filteredVg: VendorGroup = {
      ...vg,
      items: filteredItems,
      totalCost: filteredItems.reduce((sum, i) => sum + (i.lineTotal ?? i.unitCost * i.quantity), 0),
    };
    setMarkOrderedDialog({ job, vg: filteredVg });
    setMarkOrderedDate(toLocalDateInputValue(vg.submittedAt || new Date().toISOString()));
  }

  function closeMarkOrderedDialog() {
    setMarkOrderedDialog(null);
    setMarkOrderedDate(toLocalDateInputValue(new Date().toISOString()));
    setMarkOrderedWarehouseId(null);
  }

  function openMarkDeliveredDialog(job: Job, vg: VendorGroup, vendorGroups?: VendorGroup[]) {
    setMarkDeliveredDialog({ job, vg, vendorGroups });
    setMarkDeliveredDate(toLocalDateInputValue(vg.deliveredAt || new Date().toISOString()));
  }

  function closeMarkDeliveredDialog() {
    setMarkDeliveredDialog(null);
    setMarkDeliveredDate(toLocalDateInputValue(new Date().toISOString()));
  }

  async function handleMarkOrdered(job: Job, vg: VendorGroup, submittedAtOverride?: string, warehouseIdOverride?: string | null): Promise<boolean> {
    if (vg.status !== "Not Ordered" && vg.status !== "Draft") {
      toast({ title: "Order already submitted", description: `${vg.vendorName} is already ${getPOStatusLabel(vg.status)}.` });
      return false;
    }
    if (isJobInRescission(job)) {
      const days = getRescissionDaysRemaining(job.rescissionEndsAt);
      toast({
        title: "Ordering blocked — job in rescission",
        description: days > 0
          ? `This job is still in rescission (${days} day${days > 1 ? "s" : ""} remaining).`
          : "This job is still in rescission.",
        variant: "destructive",
      });
      return false;
    }
    // Check only the items being ordered (vg.items = selected/filtered items),
    // not all job materials. Unmatched items have disabled checkboxes so they
    // can't appear in vg.items via the checkbox flow; this guard catches any
    // path that skips the UI (e.g. Generate PO batch on a vendor group that
    // still has unresolved SKUs).
    const unmatched = getUnmatchedSkus(vg.items as any, resolvedRegistryProducts);
    if (unmatched.length > 0) {
      toast({
        title: "Ordering blocked — unmatched SKUs",
        description: `${unmatched.length} SKU(s) not found in Product Registry: ${unmatched.slice(0, 3).join(", ")}${unmatched.length > 3 ? ` +${unmatched.length - 3} more` : ""}. Go to Product Registry to resolve.`,
        variant: "destructive",
      });
      return false;
    }
    const lockKey = `order:${job.id}:${vg.vendorName}`;
    if (!beginPoAction(lockKey)) return false;
    const submittedAt = submittedAtOverride ?? new Date().toISOString();
    const jobLabel = `${job.customerName} – ${getProductLabel(job)}`;
    try {
      // If a Draft PO already exists (created by "Generate PO"), upgrade it to
      // Submitted in-place. Otherwise create a fresh Submitted PO.
      let createdPo: PurchaseOrder;
      if (vg.status === "Draft" && vg.poId) {
        const updated = await VendorPOService.updatePurchaseOrder(vg.poId, {
          status: "Submitted",
          submitted_at: submittedAt,
        });
        const warehouseId = warehouseIdOverride ?? markOrderedWarehouseId ?? null;
        if (warehouseId) {
          await VendorPOService.updateWarehouseAssignment(vg.poId, warehouseId);
        }
        createdPo = updated as PurchaseOrder;
      } else {
        const poNumber = generateDbSafePoNumber();
        createdPo = await VendorPOService.createPurchaseOrder({
          po_number: poNumber,
          job_id: job.id,
          job_label: jobLabel,
          vendor_id: vg.vendorId || "",
          vendor: vg.vendorName,
          status: "Submitted",
          submitted_at: submittedAt,
          total_cost: vg.totalCost,
          notes: "",
          flagged: false,
          warehouse_id: warehouseIdOverride ?? markOrderedWarehouseId ?? undefined,
          items: vg.items.map((i) => ({
            sku: i.sku,
            product_name: i.productName,
            quantity_ordered: i.quantity,
            unit_cost: i.unitCost,
          })),
        }) as PurchaseOrder;
      }
      setPurchaseOrders((prev) => {
        const withoutOld = prev.filter((p) => p.poId !== createdPo.poId);
        return [createdPo as PurchaseOrder, ...withoutOld];
      });

      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const vendorUuid = UUID_RE.test(vg.vendorId ?? "")
        ? vg.vendorId!
        : vendors.find((v) => v.vendorName.toLowerCase() === vg.vendorName.toLowerCase() && UUID_RE.test(v.vendorId ?? ""))?.vendorId ?? null;
      // Use DB-backed PO line items as the canonical source for shipment/receiving items.
      // createdPo.items comes directly from po_line_items (inserted during PO create/update),
      // so it's always the ground truth. vg.items is a client-side derivation that can be
      // stale or empty if the ordering queue was derived from an older snapshot.
      const orderedItems = (createdPo.items ?? []).length > 0 ? createdPo.items : vg.items;
      if (orderedItems.length === 0) {
        console.error("[OrderingQueue] handleMarkOrdered: no items found — shipment will be created with empty items. vg.items:", vg.items, "createdPo.items:", createdPo.items);
      }
      const shipment = await WarehouseService.createShipment({
        poId: createdPo.poId,
        poNumber: createdPo.poNumber,
        vendorId: vendorUuid,
        vendorName: vg.vendorName,
        items: orderedItems.map((i) => ({
          sku: i.sku,
          productName: i.productName,
          expectedQuantity: i.quantity,
          receivedQuantity: 0,
          jobId: job.id,
          jobLabel,
        })),
        status: "Ordered",
        expectedDelivery: new Date(new Date(submittedAt).getTime() + 7 * 86400000).toISOString(),
      });
      if (!shipment) {
        toast({ title: "PO created — shipment error", description: "The purchase order was saved but the incoming shipment could not be created. Check the console for details.", variant: "destructive" });
      }
      if (shipment) {
        setShipments((prev) => [shipment, ...prev.filter((s) => s.id !== shipment.id)]);
      }

      const receivingCreates = await Promise.all(
        orderedItems.map((i) =>
          WarehouseService.createReceivingRecord({
            poId: createdPo.poId,
            poNumber: createdPo.poNumber,
            vendorName: vg.vendorName,
            jobId: job.id,
            jobLabel,
            sku: i.sku,
            productName: i.productName,
            expectedQuantity: i.quantity,
            receivedQuantity: 0,
            status: "Pending",
            shipmentId: shipment?.id,
          }),
        ),
      );
      const createdReceiving = receivingCreates.filter((r): r is NonNullable<typeof r> => !!r);
      if (createdReceiving.length > 0) {
        setReceivingRecords((prev) => [
          ...createdReceiving,
          ...prev.filter((r) => !createdReceiving.some((cr) => cr.id === r.id)),
        ]);
      }

      const projectedOrders = [...purchaseOrders, createdPo as PurchaseOrder];
      // Re-derive vendor groups with the new PO included so we can tell whether
      // ALL vendors have been ordered (partial order) or just some.
      const projectedVendorGroups = deriveVendorGroups(
        job,
        vendors,
        projectedOrders as PurchaseOrder[],
        packets.filter((packet) => packet.jobId === job.id),
        jobAllocations,
        resolvedRegistryProducts,
        orderingVendorSkuMappings,
      );
      const allOrdered = projectedVendorGroups.every((g) => g.status !== "Not Ordered");
      const jobPatch: Partial<Job> = {
        orderStatus: allOrdered ? "Ordered" : job.orderStatus,
        materialReadiness: allOrdered ? "Ordered" : job.materialReadiness,
        procurementStatus: allOrdered ? "Ordered" : job.procurementStatus,
        planningStatus: allOrdered ? "Ordered" : job.planningStatus,
        currentStage: allOrdered ? "Ordered" : job.currentStage,
        warehouseStatus: allOrdered ? "Not Received" : job.warehouseStatus,
      };
      setJobs((prev) => prev.map((row) => (row.id === job.id ? { ...row, ...jobPatch } : row)));
      const updateResult = await JobService.update(job.id, jobPatch);
      if (updateResult.error) {
        console.warn("[OrderingQueue] Failed to sync job status after order submit:", updateResult.error);
      }

      refreshOrderingServerPages();
      toast({
        title: "Order Submitted",
        description: shipment
          ? `${createdPo.poNumber} submitted. Incoming shipment created — check Warehouse → Incoming Shipments.`
          : `${createdPo.poNumber} submitted. No incoming shipment was created (see previous error).`,
        variant: shipment ? "default" : "destructive",
      });
      return true;
    } catch (error) {
      console.error("[OrderingQueue] Failed to submit PO:", error);
      toast({ title: "Order failed", description: "Could not persist order to DB.", variant: "destructive" });
      return false;
    } finally {
      endPoAction(lockKey);
    }
  }

  async function handleConfirmMarkOrderedDialog() {
    if (!markOrderedDialog) return;
    const submittedAt = localDateInputToIso(markOrderedDate);
    if (!submittedAt) {
      toast({ title: "Order date required", description: "Select the actual vendor order date first.", variant: "destructive" });
      return;
    }
    const success = await handleMarkOrdered(markOrderedDialog.job, markOrderedDialog.vg, submittedAt);
    if (success) {
      clearVgSelection(markOrderedDialog.job.id, markOrderedDialog.vg.vendorName);
      closeMarkOrderedDialog();
    }
  }

  async function handleConfirmMarkDeliveredDialog() {
    if (!markDeliveredDialog) return;
    const deliveredAt = localDateInputToIso(markDeliveredDate);
    if (!deliveredAt) {
      toast({ title: "Delivered date required", description: "Select the actual delivered date first.", variant: "destructive" });
      return;
    }
    const success = await handleMarkDelivered(
      markDeliveredDialog.job,
      markDeliveredDialog.vg,
      markDeliveredDialog.vendorGroups,
      deliveredAt,
    );
    if (success) closeMarkDeliveredDialog();
  }

  async function handleUpdateFulfillment(
    poId: string,
    input: { externalPoNumber: string; trackingNumbers: POTrackingEntry[] },
  ) {
    const lockKey = `fulfillment:${poId}`;
    if (!beginPoAction(lockKey)) return;
    const normalizedTracking = normalizeTrackingDraft(input.trackingNumbers);
    const externalPoNumber = input.externalPoNumber.trim();
    const previous = purchaseOrders.find((po) => po.poId === poId);
    try {
      const updated = await VendorPOService.updatePurchaseOrder(poId, {
        external_po_number: externalPoNumber || null,
        tracking_numbers: normalizedTracking,
      });
      setPurchaseOrders((prev) => prev.map((po) => (po.poId === poId ? (updated as PurchaseOrder) : po)));
      refreshOrderingServerPages();
      toast({
        title: "Fulfillment updated",
        description: `${updated.poNumber} now has ${normalizedTracking.length} tracking number${normalizedTracking.length === 1 ? "" : "s"}.`,
      });
    } catch (error) {
      console.error("[OrderingQueue] Failed to update PO fulfillment:", error);
      const missingColumns = String((error as { message?: unknown })?.message ?? "").includes("external_po_number")
        || String((error as { message?: unknown })?.message ?? "").includes("tracking_numbers");
      toast({
        title: "Fulfillment update failed",
        description: missingColumns
          ? "The purchase_orders fulfillment migration has not been applied yet."
          : `Could not update ${previous?.poNumber ?? "purchase order"} fulfillment details.`,
        variant: "destructive",
      });
      throw error;
    } finally {
      endPoAction(lockKey);
    }
  }

  async function handleConfirmOrder(job: Job, vg: VendorGroup, vendorGroups?: VendorGroup[]) {
    if (!vg.poId) {
      toast({ title: "Unable to confirm", description: "Missing purchase order id.", variant: "destructive" });
      return;
    }
    if (vg.status === "Confirmed" || vg.status === "In Transit" || vg.status === "Delivered" || vg.status === "Received") {
      toast({ title: "Already confirmed", description: `${vg.poNumber || "PO"} is already ${getPOStatusLabel(vg.status)}.` });
      return;
    }
    const lockKey = `confirm:${vg.poId}`;
    if (!beginPoAction(lockKey)) return;
    try {
      const updated = await VendorPOService.updatePurchaseOrder(vg.poId, {
        status: "Confirmed",
        confirmed_at: new Date().toISOString(),
      });
      await syncShipmentStatusForPo(vg.poId, "In Transit");
      const projectedOrders = purchaseOrders.map((po) => (po.poId === updated.poId ? (updated as PurchaseOrder) : po));
      setPurchaseOrders(projectedOrders);
      const jobOrders = projectedOrders.filter((po) => po.jobId === job.id);
      const allDelivered = jobOrders.length > 0 && jobOrders.every((po) => po.status === "Delivered");
      const jobPatch: Partial<Job> = {
        orderStatus: "Confirmed",
        materialReadiness: allDelivered ? "Fully Received" : "Ordered",
        procurementStatus: "Confirmed",
        planningStatus: allDelivered ? "Ready for Install" : "Ordered",
        currentStage: allDelivered ? "Ready for Scheduling" : "Ordered",
        warehouseStatus: allDelivered ? "Received" : "Not Received",
        missingItemsList: allDelivered ? "" : job.missingItemsList,
        materialReadinessNotes: allDelivered ? "" : job.materialReadinessNotes,
      };
      setJobs((prev) => prev.map((row) => (row.id === job.id ? { ...row, ...jobPatch } : row)));
      const updateResult = await JobService.update(job.id, jobPatch);
      if (updateResult.error) {
        console.warn("[OrderingQueue] Failed to sync job status after confirm:", updateResult.error);
      }
      refreshOrderingServerPages();
      toast({ title: "Order Confirmed", description: `${updated.poNumber} confirmed for ${vg.vendorName}` });
    } catch (error) {
      console.error("[OrderingQueue] Failed to confirm PO:", error);
      toast({ title: "Confirm failed", description: "Could not confirm purchase order.", variant: "destructive" });
    } finally {
      endPoAction(lockKey);
    }
  }

  async function handleMarkDelivered(
    job: Job,
    vg: VendorGroup,
    vendorGroups?: VendorGroup[],
    deliveredAtOverride?: string,
  ): Promise<boolean> {
    if (!vg.poId) {
      toast({ title: "Unable to mark delivered", description: "Missing purchase order id.", variant: "destructive" });
      return false;
    }
    if (vg.status === "Delivered") {
      toast({ title: "Already delivered", description: `${vg.poNumber || "PO"} is already marked delivered.` });
      return false;
    }
    const unmatched = getUnmatchedSkus(job.materialsList, resolvedRegistryProducts);
    if (unmatched.length > 0) {
      toast({
        title: "Delivery confirmation blocked — unmatched SKUs",
        description: `${unmatched.length} SKU(s) not found in Product Registry: ${unmatched.slice(0, 3).join(", ")}${unmatched.length > 3 ? ` +${unmatched.length - 3} more` : ""}. Go to Product Registry to resolve.`,
        variant: "destructive",
      });
      return false;
    }
    const lockKey = `deliver:${vg.poId}`;
    if (!beginPoAction(lockKey)) return false;

    // Optimistic update — apply immediately so the UI responds without waiting for DB round-trips
    const nowIso = deliveredAtOverride ?? new Date().toISOString();
    const optimisticOrders = purchaseOrders.map((po) =>
      po.poId === vg.poId ? { ...po, status: "Delivered" as const, deliveredAt: nowIso } : po
    );
    const jobOrders = optimisticOrders.filter((po) => po.jobId === job.id);
    const groupDeliveryState = deliveryStateForGroups(vendorGroups, vg);
    const allDelivered = groupDeliveryState?.allDelivered ?? (jobOrders.length > 0 && jobOrders.every((po) => po.status === "Delivered"));
    const anyDelivered = groupDeliveryState?.anyDelivered ?? jobOrders.some((po) => po.status === "Delivered");
    const jobPatch: Partial<Job> = {
      orderStatus: allDelivered ? "Delivered" : "Confirmed",
      materialReadiness: allDelivered ? "Fully Received" : anyDelivered ? "Partially Received" : "Ordered",
      procurementStatus: "Delivered",
      planningStatus: allDelivered ? "Receiving" : "Ordered",
      currentStage: allDelivered ? "Ready for Scheduling" : "Ordered",
      warehouseStatus: allDelivered ? "Received" : "Partially Received",
      missingItemsList: allDelivered ? "" : job.missingItemsList,
      materialReadinessNotes: allDelivered ? "" : job.materialReadinessNotes,
    };
    // For the DB write, exclude materialReadiness and related fields — those are
    // computed authoritatively by syncJobMaterialReadiness (called inside
    // syncReceivingRecordsForPoDelivery). Writing them here in parallel would race
    // and potentially write "Fully Received" for a partial order.
    const { materialReadiness: _mr, missingItemsList: _mil, materialReadinessNotes: _mrn, ...dbPatch } = jobPatch;
    setPurchaseOrders(optimisticOrders);
    setJobs((prev) => prev.map((row) => (row.id === job.id ? { ...row, ...jobPatch } : row)));

    try {
      // Fire all DB writes in parallel.
      // NOTE: syncReceivingRecordsForPoDelivery is intentionally NOT called here.
      // The warehouse team completes receiving via Incoming Shipments → "Receive Shipment".
      const [updated] = await Promise.all([
        VendorPOService.updatePurchaseOrder(vg.poId, { status: "Delivered", delivered_at: nowIso }),
        syncShipmentStatusForPo(vg.poId, "Delivered", nowIso),
        JobService.update(job.id, dbPatch).then((result) => {
          if (result?.error) console.warn("[OrderingQueue] Failed to sync job status after delivery:", result.error);
        }),
      ]);
      // Reconcile PO state with confirmed DB response
      setPurchaseOrders((prev) => prev.map((po) => (po.poId === updated.poId ? (updated as PurchaseOrder) : po)));
      refreshOrderingServerPages();
      toast({
        title: "Order Delivered",
        description: `${updated.poNumber} marked delivered for ${vg.vendorName} on ${formatShortDate(nowIso)}.`,
      });
      return true;
    } catch (error) {
      // Roll back optimistic changes on failure
      setPurchaseOrders((prev) =>
        prev.map((po) =>
          po.poId === vg.poId
            ? { ...po, status: vg.status === "Not Ordered" ? po.status : vg.status }
            : po,
        ),
      );
      setJobs((prev) => prev.map((row) => (row.id === job.id ? job : row)));
      console.error("[OrderingQueue] Failed to mark PO delivered:", error);
      toast({ title: "Deliver failed", description: "Could not mark purchase order delivered.", variant: "destructive" });
      return false;
    } finally {
      endPoAction(lockKey);
    }
  }

  async function handlePoTaxChange(poId: string, tax: number, subtotal: number) {
    setPoTaxOverrides((prev) => ({ ...prev, [poId]: tax }));
    await supabase
      .from("purchase_orders")
      .update({ tax, total_cost: subtotal + tax })
      .eq("id", poId);
  }

  function handleCopyOrder(vg: VendorGroup, job: Job) {
    const text = vg.items.map(i => `${i.productName} (${i.sku}) × ${i.quantity}`).join("\n");
    const taxLine = (vg.tax ?? 0) > 0 ? `\nSubtotal: $${(vg.subtotal ?? vg.totalCost ?? 0).toLocaleString()}\nTax: $${(vg.tax ?? 0).toLocaleString()}` : "";
    const full = `Order for ${job.customerName}\nVendor: ${vg.vendorName}\n\n${text}${taxLine}\n\nTotal: $${vg.totalCost.toLocaleString()}`;
    navigator.clipboard.writeText(full);
    toast({ title: "Copied to clipboard" });
  }

  function handleDownloadPDF(vg: VendorGroup, job: Job) {
    try {
      const pdf = new jsPDF("p", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 28;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      const titleLabel = vg.poNumber
        ? `Purchase Order #${vg.poNumber}`
        : "Purchase Order (Draft)";
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.text(titleLabel, pageWidth / 2, y, { align: "center" });
      y += 18;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(120);
      pdf.text(
        `Generated: ${new Date().toLocaleDateString()}  ·  Koredor`,
        pageWidth / 2,
        y,
        { align: "center" },
      );
      y += 15;
      pdf.setTextColor(0);

      pdf.setDrawColor(200);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 15;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("JOB DETAILS", margin, y);
      y += 8;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      const jobNumber = job.jobNumber ?? "—";
      const customerName = job.customerName ?? "Unknown";
      pdf.text(`Job #:      ${jobNumber}`, margin, y);
      y += 6;
      pdf.text(`Customer:   ${customerName}`, margin, y);
      y += 6;
      const jobAddress = job.address?.street || job.address?.formatted || "—";
      pdf.text(`Address:    ${jobAddress}`, margin, y);
      y += 10;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("VENDOR", margin, y);
      y += 8;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(`Name:       ${vg.vendorName}`, margin, y);
      y += 6;
      pdf.text(`Email:      ${vg.vendorEmail}`, margin, y);
      y += 6;
      pdf.text(`Method:     ${vg.orderingMethod}`, margin, y);
      y += 6;
      pdf.text(`Status:     ${vg.status}`, margin, y);
      y += 12;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("ORDER ITEMS", margin, y);
      y += 6;

      autoTable(pdf, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [["SKU", "Product Name", "Qty", "Unit Cost", "Line Total"]],
        body: vg.items.map((item) => [
          item.sku,
          item.productName,
          item.quantity.toString(),
          `$${item.unitCost.toLocaleString()}`,
          `$${(item.unitCost * item.quantity).toLocaleString()}`,
        ]),
        theme: "striped",
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 180 },
          2: { cellWidth: 40, halign: "center" },
          3: { cellWidth: 80, halign: "right" },
          4: { cellWidth: 100, halign: "right" },
        },
      });

      const tableY =
        (pdf as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable
          .finalY + 10;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      let pdfTotalY = tableY;
      if ((vg.tax ?? 0) > 0) {
        pdf.setFont("helvetica", "normal");
        pdf.text(`Subtotal: $${(vg.subtotal ?? vg.totalCost ?? 0).toLocaleString()}`, pageWidth - margin, pdfTotalY, { align: "right" });
        pdfTotalY += 14;
        pdf.text(`Tax: $${(vg.tax ?? 0).toLocaleString()}`, pageWidth - margin, pdfTotalY, { align: "right" });
        pdfTotalY += 14;
        pdf.setFont("helvetica", "bold");
      }
      pdf.text(`Total: $${vg.totalCost.toLocaleString()}`, pageWidth - margin, pdfTotalY, {
        align: "right",
      });

      if (tableY > 780) {
        pdf.addPage();
      }

      const safeFileName = `${(vg.vendorName?.replace(/[^a-z0-9]/gi, "_") ?? "Vendor")}_${job.jobNumber ?? "Job"}_PO.pdf`;
      pdf.save(safeFileName);

      toast({
        title: "PDF Downloaded",
        description: `${vg.vendorName} purchase order for ${job.customerName}`,
      });
    } catch (error) {
      toast({
        title: "PDF Error",
        description: error instanceof Error ? error.message : "Failed to generate PDF.",
        variant: "destructive",
      });
    }
  }

  function handleSendEmail(vg: VendorGroup, job: Job) {
    const subject = encodeURIComponent(`Order – ${job.customerName} – ${getProductLabel(job)}`);
    const body = encodeURIComponent(vg.items.map(i => `${i.productName} × ${i.quantity}`).join("\n"));
    window.open(`mailto:${vg.vendorEmail}?subject=${subject}&body=${body}`);
  }

  // Create Draft POs for every not-yet-ordered vendor group on this job.
  // Does NOT mark as ordered — user must click "Mark Ordered" per vendor after placing the order.
  async function handleGenerateAllPOs(job: Job, vendorGroups: VendorGroup[]) {
    const pending = vendorGroups.filter(vg => vg.status === "Not Ordered");
    if (pending.length === 0) {
      toast({ title: "Nothing to generate", description: "All vendor packages are already generated or ordered." });
      return;
    }
    const batchLockKey = `order-all:${job.id}`;
    if (!beginPoAction(batchLockKey)) return;
    let successCount = 0;
    try {
      for (const vg of pending) {
        const selectedItems = getVgSelectedItems(job.id, vg.vendorName, vg.items);
        const items = selectedItems.length > 0 ? selectedItems : vg.items;
        const poNumber = generateDbSafePoNumber();
        const jobLabel = `${job.customerName} – ${getProductLabel(job)}`;
        try {
          const createdPo = await VendorPOService.createPurchaseOrder({
            po_number: poNumber,
            job_id: job.id,
            job_label: jobLabel,
            vendor_id: vg.vendorId || "",
            vendor: vg.vendorName,
            status: "Draft",
            total_cost: items.reduce((sum, i) => sum + i.unitCost * i.quantity, 0),
            notes: "",
            flagged: false,
            items: items.map((i) => ({
              sku: i.sku,
              product_name: i.productName,
              quantity_ordered: i.quantity,
              unit_cost: i.unitCost,
            })),
          });
          setPurchaseOrders((prev) => {
            const withoutOld = prev.filter((p) => p.poId !== createdPo.poId);
            return [createdPo as PurchaseOrder, ...withoutOld];
          });
          successCount += 1;
        } catch (err) {
          console.error("[OrderingQueue] Failed to create Draft PO for", vg.vendorName, err);
        }
      }
    } finally {
      endPoAction(batchLockKey);
    }
    refreshOrderingServerPages();
    if (successCount === pending.length) {
      toast({ title: "POs Generated", description: `${successCount} draft PO${successCount > 1 ? "s" : ""} created. Click "Mark Ordered" per vendor after placing the order.` });
    } else if (successCount > 0) {
      toast({ title: "Partial generation", description: `${successCount} of ${pending.length} draft POs created.`, variant: "destructive" });
    } else {
      toast({ title: "Generation failed", description: "No draft POs could be created.", variant: "destructive" });
    }
  }

  async function handleReverseOrder(job: Job, vg: VendorGroup): Promise<void> {
    if (!vg.poId) return;
    const lockKey = `reverse:${vg.poId}`;
    if (!beginPoAction(lockKey)) return;
    try {
      // Explicit deletes first (shipments + receiving_records use SET NULL, not CASCADE)
      await Promise.all([
        WarehouseService.deleteShipmentsForPo(vg.poId),
        WarehouseService.deleteReceivingRecordsForPo(vg.poId),
      ]);

      // Delete the PO — cascades to po_line_items automatically
      await VendorPOService.deletePurchaseOrder(vg.poId);

      // If delivered, clean up allocations created by reconciliation for this PO's SKUs
      if (vg.status === "Delivered") {
        await WarehouseService.deleteJobAllocationsForPoReversal(job.id, vg.items.map((i) => i.sku));
      }

      // Compute remaining POs for this job before updating state
      const remainingPOs = purchaseOrders.filter((po) => po.jobId === job.id && po.poId !== vg.poId);

      // Remove from local state
      setPurchaseOrders((prev) => prev.filter((po) => po.poId !== vg.poId));
      setShipments((prev) => prev.filter((s) => s.poId !== vg.poId));
      setReceivingRecords((prev) => prev.filter((r) => r.poId !== vg.poId));

      // Reset job ordering fields if no POs remain
      if (remainingPOs.length === 0) {
        const jobPatch: Partial<Job> = {
          orderStatus: "Not Ordered" as Job["orderStatus"],
          materialReadiness: "Not Ordered",
          procurementStatus: "Not Ordered" as Job["procurementStatus"],
          warehouseStatus: "Not Started" as Job["warehouseStatus"],
        };
        setJobs((prev) => prev.map((row) => (row.id === job.id ? { ...row, ...jobPatch } : row)));
        const updateResult = await JobService.update(job.id, jobPatch);
        if (updateResult.error) {
          console.warn("[OrderingQueue] Failed to reset job status after order reversal:", updateResult.error);
        }
      }

      refreshOrderingServerPages();
      setReverseOrderDialog(null);
      toast({
        title: "Order Reversed",
        description: `${vg.poNumber ?? "PO"} for ${vg.vendorName} has been pulled back. The job is reset to Not Ordered.`,
      });
    } catch (error) {
      console.error("[OrderingQueue] Failed to reverse order:", error);
      toast({
        title: "Reverse failed",
        description: "Could not reverse the purchase order. Please try again.",
        variant: "destructive",
      });
    } finally {
      endPoAction(lockKey);
    }
  }

  /* ── Material selection helpers (per vendor group) ────────────────────── */

  function vgKey(jobId: string, vendorName: string) {
    return `${jobId}:${vendorName}`;
  }

  function isVgItemSelected(jobId: string, vendorName: string, sku: string): boolean {
    const sel = selectedMaterials[vgKey(jobId, vendorName)];
    return sel === undefined || sel.has(sku);
  }

  function getVgSelectedItems(jobId: string, vendorName: string, items: VendorGroup["items"]): VendorGroup["items"] {
    const sel = selectedMaterials[vgKey(jobId, vendorName)];
    return sel === undefined ? items : items.filter((i) => sel.has(i.sku));
  }

  function toggleVgItem(jobId: string, vendorName: string, sku: string, allSkus: string[]) {
    const key = vgKey(jobId, vendorName);
    setSelectedMaterials((prev) => {
      const base = prev[key] ?? new Set(allSkus);
      const next = new Set(base);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      if (allSkus.every((s) => next.has(s))) {
        const cleaned = { ...prev };
        delete cleaned[key];
        return cleaned;
      }
      return { ...prev, [key]: next };
    });
  }

  function toggleAllVgItems(jobId: string, vendorName: string, selectableSkus: string[]) {
    const key = vgKey(jobId, vendorName);
    setSelectedMaterials((prev) => {
      const current = prev[key];
      const allSelected = current === undefined || selectableSkus.every((s) => current.has(s));
      if (allSelected) return { ...prev, [key]: new Set<string>() };
      const cleaned = { ...prev };
      delete cleaned[key];
      return cleaned;
    });
  }

  function clearVgSelection(jobId: string, vendorName: string) {
    const key = vgKey(jobId, vendorName);
    setSelectedMaterials((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function handleRepairOverAllocations() {
    if (repairingAllocations) return;
    setRepairingAllocations(true);
    try {
      const result = await WarehouseService.repairOverAllocations();
      await Promise.all([refreshWarehouse(), refreshJobs()]);
      refreshOrderingServerPages();
      toast({
        title: "Allocation repair complete",
        description: `${result.jobsScanned} jobs scanned · ${result.rowsTouched} rows adjusted · ${result.quantityTrimmed} qty trimmed`,
      });
    } catch (error) {
      console.error("[OrderingQueue] Failed to run allocation repair:", error);
      toast({
        title: "Allocation repair failed",
        description: "Could not complete over-allocation repair.",
        variant: "destructive",
      });
    } finally {
      setRepairingAllocations(false);
    }
  }

  function getOverallStatus(vendorGroups: VendorGroup[]): string {
    if (vendorGroups.every(vg => vg.status === "Delivered" || vg.status === "Received")) return "Delivered";
    if (vendorGroups.every(vg => vg.status !== "Not Ordered")) return "In Progress";
    if (vendorGroups.some(vg => vg.status !== "Not Ordered")) return "Partially Ordered";
    return "Not Ordered";
  }

  // Action-first: derive top "Needs Action" items from the queue
  const actionItems: ActionRequiredItem[] = useMemo(() => {
    const items: ActionRequiredItem[] = [];
    for (const e of enrichedJobs) {
      if (isJobInRescission(e.job)) {
        const days = getRescissionDaysRemaining(e.job.rescissionEndsAt);
        items.push({
          id: `${e.job.id}-rescission-block`,
          severity: "hard",
          label: `${e.job.customerName} — ordering blocked (rescission)`,
          detail: days > 0
            ? `${getProductLabel(e.job)} · ${days} day${days > 1 ? "s" : ""} remaining`
            : `${getProductLabel(e.job)} · rescission active`,
          primary: {
            label: "Open Job",
            executionMode: "navigate",
            route: `/jobs/${e.job.id}`,
          },
        });
        continue;
      }
      const pendingCount = e.vendorGroups.filter(vg => vg.status === "Not Ordered").length;
      if (pendingCount === 0) continue;
      const jobUnmatched = resolvedRegistryProducts.length > 0 && getUnmatchedSkus(e.job.materialsList, resolvedRegistryProducts).length > 0;
      items.push({
        id: `${e.job.id}-pending`,
        severity: pendingCount === e.vendorGroups.length ? "hard" : "warning",
        label: `${e.job.customerName} — ${pendingCount} vendor${pendingCount > 1 ? "s" : ""} pending`,
        detail: getProductLabel(e.job),
        primary: {
          label: "Generate PO",
          executionMode: "direct",
          handler: () => { void handleGenerateAllPOs(e.job, e.vendorGroups); },
          disabled: jobUnmatched,
          disabledTitle: jobUnmatched ? "Unmatched SKUs — resolve in Product Registry first" : undefined,
        },
        secondaryHref: `/jobs/${e.job.id}`,
      });
    }
    return items;
  }, [enrichedJobs]);

  const overallStatusColor: Record<string, string> = {
    "Not Ordered": "text-muted-foreground",
    "Partially Ordered": "text-warning dark:text-warning",
    "In Progress": "text-info dark:text-info",
    "Delivered": "text-success dark:text-success",
  };

  // Partition for "Three Sections" view based on draft status.
  // Ready to Prep = no draft or voided · Prepared = draft.status === "prepared" · Pushed = draft.status === "pushed" OR has any submitted PO.
  const partitioned = useMemo(() => {
    const readyToPrep: typeof filteredJobs = [];
    const prepared: typeof filteredJobs = [];
    const pushed: typeof filteredJobs = [];
    for (const entry of filteredJobs) {
      const d = drafts[entry.job.id];
      const hasSubmittedPo = entry.vendorGroups.some(vg => vg.status !== "Not Ordered");
      if (d?.status === "pushed" || hasSubmittedPo) pushed.push(entry);
      else if (d?.status === "prepared") prepared.push(entry);
      else readyToPrep.push(entry);
    }
    return { readyToPrep, prepared, pushed };
  }, [filteredJobs, drafts]);

  // Single source of truth for the row body (used by both view modes).
  const renderJobRow = (
    { job, vendorGroups, bomSource, pos }: typeof filteredJobs[number],
    options: { forceExpanded?: boolean } = {},
  ) => {
    const forceExpanded = options.forceExpanded === true;
    const isExpanded = forceExpanded || expandedJob === job.id;
    const overall = getOverallStatus(vendorGroups);
    const draft = drafts[job.id];
    const poCount = vendorGroups.filter((group) => Boolean(group.poId)).length;
    const pendingPoCount = vendorGroups.length - poCount;
    const jobHasUnmatchedSkus = resolvedRegistryProducts.length > 0 && getUnmatchedSkus(job.materialsList, resolvedRegistryProducts).length > 0;
    return (
      <Card key={job.id} className="overflow-hidden">
        <Collapsible
          open={isExpanded}
          onOpenChange={() => {
            if (forceExpanded) return;
            setExpandedJob(isExpanded ? null : job.id);
          }}
        >
          <CollapsibleTrigger asChild>
            <div
              className={cn(
                "flex items-center gap-4 p-4 transition-colors",
                forceExpanded ? "cursor-default bg-muted/20" : "cursor-pointer hover:bg-muted/30",
              )}
            >
              {forceExpanded ? (
                <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{job.customerName}</span>
                  <Badge variant="outline" className="text-xs">{getJobDisplayRef(job)}</Badge>
                  {draft?.status === "prepared" && (
                    <Badge className="text-[10px] bg-info/15 text-info border-info/30">Prepared</Badge>
                  )}
                  {draft?.status === "voided" && (
                    <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Draft Voided</Badge>
                  )}
                  {bomSource === "inferred" && (
                    <Badge variant="outline" className="text-[10px] text-warning border-warning/40">
                      {getJobBomSourceLabel(bomSource)}
                    </Badge>
                  )}
                  {bomSource === "none" && (
                    <Badge variant="outline" className="text-[10px] text-destructive border-destructive/40">
                      {getJobBomSourceLabel(bomSource)}
                    </Badge>
                  )}
                  {(() => {
                    const unmatched = getUnmatchedSkus(job.materialsList, resolvedRegistryProducts);
                    return unmatched.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-destructive border border-destructive/30 bg-destructive/10 rounded px-1.5 py-0.5">
                        <AlertTriangle className="h-3 w-3" />
                        {unmatched.length} unmatched SKU{unmatched.length > 1 ? "s" : ""}
                      </span>
                    ) : null;
                  })()}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{getProductLabel(job)}</p>
              </div>
              <div className="hidden md:flex items-center gap-4 text-xs">
                  <span className="text-muted-foreground">Approved: {formatShortDate(job.activityHistory?.[0]?.date)}</span>
                {job.scheduledInstallDate && (
                  <span className="text-muted-foreground">Install: {formatShortDate(job.scheduledInstallDate)}</span>
                )}
              </div>
              <span className={`text-xs font-medium ${overallStatusColor[overall]}`}>{overall}</span>
              <Badge variant="secondary" className="text-xs">
                {vendorGroups.length} vendor{vendorGroups.length !== 1 ? "s" : ""}
              </Badge>
              {vendorGroups.some(vg => vg.status === "Not Ordered") && (
                <PermissionGate permission="ordering_create">
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1"
                    disabled={isPoActionInFlight(`order-all:${job.id}`) || jobHasUnmatchedSkus}
                    onClick={(e) => { e.stopPropagation(); void handleGenerateAllPOs(job, vendorGroups); }}
                    title={jobHasUnmatchedSkus ? "Unmatched SKUs — resolve in Product Registry first" : undefined}
                  >
                    <Zap className="h-3 w-3" />
                    Generate PO
                  </Button>
                </PermissionGate>
              )}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-t bg-muted/10 p-4 space-y-3">
              <DraftSpecDriftPanel job={job} draft={draft} />
              {vendorGroups.map((vg, idx) => (
                <Card key={vg.poId ?? `${job.id}-pending-${vg.vendorName}-${idx}`} className="border">
                  <CardHeader className="p-3 pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <CardTitle className="text-sm">
                          {vg.vendorName}
                        </CardTitle>
                        <Badge variant="outline" className="text-[10px]">{vg.orderingMethod}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {vg.poNumber && <span className="text-xs text-muted-foreground">{vg.poNumber}</span>}
                        <Badge className={`text-[10px] ${STATUS_COLORS[vg.status]}`}>{getPOStatusLabel(vg.status)}</Badge>
                        {(() => {
                          const linked = vg.poId ? shipments.find((s) => s.poId === vg.poId) : undefined;
                          if (!linked || linked.status === "Ordered" || linked.status === "Received" || linked.status === "Delivered") return null;
                          const shipmentStyle = linked.status === "In Transit"
                            ? "bg-info/15 text-info dark:bg-info/30 dark:text-info"
                            : "bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning";
                          return (
                            <Badge className={`text-[10px] ${shipmentStyle}`}>
                              {linked.status}
                            </Badge>
                          );
                        })()}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    {(() => {
                      const orderLockKey = `order:${job.id}:${vg.vendorName}`;
                      const confirmLockKey = vg.poId ? `confirm:${vg.poId}` : "";
                      const deliverLockKey = vg.poId ? `deliver:${vg.poId}` : "";
                      const fulfillmentLockKey = vg.poId ? `fulfillment:${vg.poId}` : "";
                      const anyActionBusy =
                        isPoActionInFlight(orderLockKey) ||
                        (confirmLockKey ? isPoActionInFlight(confirmLockKey) : false) ||
                        (deliverLockKey ? isPoActionInFlight(deliverLockKey) : false) ||
                        (fulfillmentLockKey ? isPoActionInFlight(fulfillmentLockKey) : false);
                      return (
                        <>
                    {(() => {
                      const isNotOrdered = vg.status === "Not Ordered";
                      const matchedSkus = vg.items
                        .map((i) => i.sku)
                        .filter((sku) => resolvedRegistryProducts.length === 0 || resolvedRegistryProducts.some((p) => normalizeMaterialSku(p.sku) === normalizeMaterialSku(sku)));
                      const allSelectableSelected = matchedSkus.length > 0 && matchedSkus.every((sku) => isVgItemSelected(job.id, vg.vendorName, sku));
                      const someSelected = matchedSkus.some((sku) => isVgItemSelected(job.id, vg.vendorName, sku));
                      return (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {isNotOrdered && (
                                <TableHead className="w-8 h-8">
                                  <Checkbox
                                    checked={allSelectableSelected ? true : someSelected ? "indeterminate" : false}
                                    onCheckedChange={() => toggleAllVgItems(job.id, vg.vendorName, matchedSkus)}
                                    disabled={matchedSkus.length === 0}
                                    aria-label="Select all materials"
                                  />
                                </TableHead>
                              )}
                              <TableHead className="text-xs h-8">SKU</TableHead>
                              <TableHead className="text-xs h-8">Product</TableHead>
                              <TableHead className="text-xs h-8 text-right">Qty</TableHead>
                              <TableHead className="text-xs h-8 text-right">Unit Cost</TableHead>
                              <TableHead className="text-xs h-8 text-right">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vg.items.map((item, i) => {
                              const isMatched = resolvedRegistryProducts.length === 0 || resolvedRegistryProducts.some((p) => normalizeMaterialSku(p.sku) === normalizeMaterialSku(item.sku));
                              const isSelected = isVgItemSelected(job.id, vg.vendorName, item.sku);
                              return (
                                <TableRow key={i} className={cn(isNotOrdered && !isSelected && isMatched ? "opacity-50" : "")}>
                                  {isNotOrdered && (
                                    <TableCell className="py-1.5 w-8">
                                      <Checkbox
                                        checked={isMatched ? isSelected : false}
                                        disabled={!isMatched}
                                        onCheckedChange={() => {
                                          if (isMatched) toggleVgItem(job.id, vg.vendorName, item.sku, matchedSkus);
                                        }}
                                        aria-label={`Select ${item.sku}`}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell className="text-xs py-1.5 font-mono">{item.sku}</TableCell>
                                  <TableCell className="text-xs py-1.5">{resolveProductDisplayName(item)}</TableCell>
                                  <TableCell className="text-xs py-1.5 text-right">{item.quantity}</TableCell>
                                  <TableCell className="text-xs py-1.5 text-right">${item.unitCost.toLocaleString()}</TableCell>
                                  <TableCell className="text-xs py-1.5 text-right font-medium">${(item.unitCost * item.quantity).toLocaleString()}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      );
                    })()}
                    {vg.poId && (
                      <POFulfillmentPanel
                        vg={vg}
                        disabled={anyActionBusy}
                        onSave={handleUpdateFulfillment}
                      />
                    )}
                    {vg.poId && (() => {
                      // Read warehouseId from purchaseOrders context (updates optimistically)
                      // so the dropdown reflects changes immediately without waiting for view refresh.
                      const liveWarehouseId =
                        purchaseOrders.find((po) => po.poId === vg.poId)?.warehouseId
                        ?? vg.warehouseId
                        ?? null;
                      return (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-muted-foreground shrink-0">Warehouse:</span>
                          <div className="flex-1 max-w-[220px]">
                            <WarehouseSelector
                              value={liveWarehouseId}
                              onChange={async (warehouseId) => {
                                if (!vg.poId) return;
                                try {
                                  await VendorPOService.updateWarehouseAssignment(vg.poId, warehouseId);
                                  setPurchaseOrders((prev) =>
                                    prev.map((po) =>
                                      po.poId === vg.poId ? { ...po, warehouseId } : po,
                                    ),
                                  );
                                  refreshOrderingServerPages();
                                } catch {
                                  toast({ title: "Failed to update warehouse", variant: "destructive" });
                                }
                              }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t">
                      <div className="flex flex-col gap-0.5">
                        {(() => {
                          const poId = vg.poId ?? "";
                          const subtotal = vg.subtotal ?? vg.totalCost ?? 0;
                          const tax = poTaxOverrides[poId] ?? vg.tax ?? 0;
                          const total = subtotal + tax;
                          return (
                            <>
                              <span className="text-[11px] text-muted-foreground">Subtotal: ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                              <div className="flex items-center gap-1.5 text-[11px]">
                                <label className="text-muted-foreground" htmlFor={`tax-${poId}`}>Sales Tax ($)</label>
                                <input
                                  id={`tax-${poId}`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={tax || ""}
                                  placeholder="0.00"
                                  disabled={!poId}
                                  onChange={(e) => {
                                    const val = Math.max(0, parseFloat(e.target.value) || 0);
                                    if (poId) void handlePoTaxChange(poId, val, subtotal);
                                  }}
                                  className="w-20 h-5 text-right text-[11px] tabular-nums border border-input rounded px-2 bg-background"
                                />
                              </div>
                              <span className="text-xs font-medium">Total: ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </>
                          );
                        })()}
                        {vg.submittedAt && (
                          <span className="text-[11px] text-muted-foreground">
                            Ordered: {formatShortDate(vg.submittedAt)}
                          </span>
                        )}
                        {vg.deliveredAt && (
                          <span className="text-[11px] text-muted-foreground">
                            Delivered: {formatShortDate(vg.deliveredAt)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => handleCopyOrder(vg, job)} disabled={anyActionBusy}>
                          <Copy className="h-3 w-3" /> Copy
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => handleDownloadPDF(vg, job)} disabled={anyActionBusy}>
                          <Download className="h-3 w-3" /> PDF
                        </Button>
                        {vg.vendorEmail && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => handleSendEmail(vg, job)} disabled={anyActionBusy}>
                            <Mail className="h-3 w-3" /> Email
                          </Button>
                        )}
                        {vg.status !== "Not Ordered" && vg.poId && (
                          <PermissionGate permission="ordering_approve">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                              disabled={anyActionBusy || isPoActionInFlight(`reverse:${vg.poId}`)}
                              title="Reverse this order — deletes PO and resets job to Not Ordered"
                              onClick={() => setReverseOrderDialog({ job, vg })}
                            >
                              <RotateCcw className="h-3 w-3" />
                              {isPoActionInFlight(`reverse:${vg.poId}`) ? "Reversing..." : "Reverse"}
                            </Button>
                          </PermissionGate>
                        )}
                        {(vg.status === "Not Ordered" || vg.status === "Draft") && (
                          (() => {
                            const selectedItems = getVgSelectedItems(job.id, vg.vendorName, vg.items);
                            const hasSelection = vg.status === "Draft" || selectedItems.length > 0;
                            const allItems = vg.items.length;
                            const countLabel = vg.status === "Not Ordered" && selectedItems.length > 0 && selectedItems.length < allItems
                              ? ` (${selectedItems.length}/${allItems})`
                              : "";
                            return (
                              <PermissionGate permission="ordering_create">
                                <Button
                                  size="sm"
                                  className="h-7 text-xs gap-1"
                                  disabled={isJobInRescission(job) || anyActionBusy || !hasSelection}
                                  onClick={() => { openMarkOrderedDialog(job, vg, vg.status === "Draft" ? vg.items : selectedItems); }}
                                  title={isJobInRescission(job) ? "Blocked while job is in rescission." : !hasSelection ? "Select at least one material to order." : undefined}
                                >
                                  <CheckCircle2 className="h-3 w-3" />
                                  {isJobInRescission(job) ? "Blocked (Rescission)" : anyActionBusy ? "Processing..." : `Mark Ordered${countLabel}`}
                                </Button>
                              </PermissionGate>
                            );
                          })()
                        )}
                        {vg.status === "Submitted" && (
                          <PermissionGate permission="ordering_approve">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1"
                              disabled={anyActionBusy || jobHasUnmatchedSkus}
                              title={jobHasUnmatchedSkus ? "Unmatched SKUs — resolve in Product Registry first" : undefined}
                              onClick={() => { void handleConfirmOrder(job, vg, vendorGroups); }}
                            >
                              <Clock className="h-3 w-3" /> {anyActionBusy ? "Processing..." : "Confirm"}
                            </Button>
                          </PermissionGate>
                        )}
                        {(vg.status === "Confirmed" || vg.status === "In Transit" || isPoActionInFlight(deliverLockKey)) && (
                          <PermissionGate permission="ordering_approve">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1"
                              disabled={anyActionBusy || jobHasUnmatchedSkus}
                              title={jobHasUnmatchedSkus ? "Unmatched SKUs — resolve in Product Registry first" : undefined}
                              onClick={() => { openMarkDeliveredDialog(job, vg, vendorGroups); }}
                            >
                              <Truck className={cn("h-3 w-3", isPoActionInFlight(deliverLockKey) && "kdr-truck-deliver")} />
                              {isPoActionInFlight(deliverLockKey) ? (
                                <span className="inline-flex gap-px">
                                  Delivering
                                  <span className="animate-pulse" style={{ animationDelay: "0ms" }}>.</span>
                                  <span className="animate-pulse" style={{ animationDelay: "200ms" }}>.</span>
                                  <span className="animate-pulse" style={{ animationDelay: "400ms" }}>.</span>
                                </span>
                              ) : "Delivered"}
                            </Button>
                          </PermissionGate>
                        )}
                      </div>
                    </div>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  const focusedJobModalEntry = focusedJobModalId
    ? filteredJobs.find((entry) => entry.job.id === focusedJobModalId)
      ?? partitioned.readyToPrep.find((entry) => entry.job.id === focusedJobModalId)
      ?? null
    : null;

  const renderJobModalLauncher = ({ job, vendorGroups, bomSource }: typeof filteredJobs[number]) => {
    const overall = getOverallStatus(vendorGroups);
    const draft = drafts[job.id];
    const poCount = vendorGroups.filter((group) => Boolean(group.poId)).length;
    const pendingPoCount = Math.max(0, vendorGroups.length - poCount);

    return (
      <Card key={job.id} className="overflow-hidden border-primary/20 bg-primary/[0.02]">
        <button
          type="button"
          className="w-full p-4 text-left transition-colors hover:bg-primary/[0.05]"
          onClick={() => setFocusedJobModalId(job.id)}
        >
          <div className="flex items-center gap-4">
            <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-sm">{job.customerName}</span>
                <Badge variant="outline" className="text-xs">{getJobDisplayRef(job)}</Badge>
                {draft?.status === "voided" && (
                  <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Draft Voided</Badge>
                )}
                {bomSource === "inferred" && (
                  <Badge variant="outline" className="text-[10px] text-warning border-warning/40">
                    {getJobBomSourceLabel(bomSource)}
                  </Badge>
                )}
                {bomSource === "none" && (
                  <Badge variant="outline" className="text-[10px] text-destructive border-destructive/40">
                    {getJobBomSourceLabel(bomSource)}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{getProductLabel(job)}</p>
            </div>
            <div className="hidden md:flex items-center gap-3 text-xs">
              <span className="text-muted-foreground">
                {pendingPoCount} pending vendor{pendingPoCount === 1 ? "" : "s"}
              </span>
              <span className={`font-medium ${overallStatusColor[overall]}`}>{overall}</span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {vendorGroups.length} vendor{vendorGroups.length !== 1 ? "s" : ""}
            </Badge>
            <span className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary">
              Open Job
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </button>
      </Card>
    );
  };

  const sectionConfig: { key: keyof typeof partitioned; label: string; icon: typeof ClipboardCheck; tone: string }[] = [
    { key: "readyToPrep", label: "Ready to Prep", icon: ClipboardList, tone: "text-muted-foreground" },
    { key: "prepared",    label: "Prepared — Awaiting Push", icon: ClipboardCheck, tone: "text-info" },
    { key: "pushed",      label: "Pushed to Vendor", icon: Send, tone: "text-success" },
  ];

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Ordering"
        right={
          <div className="flex items-center gap-4">
            <HeroMetric label="Active Packets" value={activePacketCount} tone={exceptionPacketCount > 0 ? "warn" : "default"} />
            <HeroDivider />
            <HeroMetric label="Exceptions" value={exceptionPacketCount} tone={exceptionPacketCount > 0 ? "warn" : "default"} />
            <HeroDivider />
            <HeroMetric label="Jobs in Queue" value={counts.all} />
            <HeroDivider />
            <HeroMetric label="Delivered" value={counts.delivered} tone="good" />
            <HeroDivider />
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/purchase-orders")}
              className="gap-1.5"
            >
              <ClipboardList className="h-3.5 w-3.5" />
              All POs
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs gap-1.5"
              onClick={() => setShowPlaceOrderModal(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Place Order
            </Button>
            <ReportFrictionButton moduleName="Ordering" />
          </div>
        }
      />

      {/* Top-level lane switch — Packets (manual rep workflow) vs Jobs (legacy PO grouping) */}
      <div className="flex items-center gap-2 border-b border-border/60 pb-2">
        <button
          onClick={() => setTopLane("packets")}
          className={cn(
            "px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all",
            topLane === "packets"
              ? "bg-primary/15 text-primary ring-1 ring-primary/30"
              : "text-muted-foreground hover:bg-muted/40",
          )}
        >
          <Package className="h-3.5 w-3.5 inline mr-1.5" />
          Packets ({activePacketCount})
        </button>
        <button
          onClick={() => setTopLane("jobs")}
          className={cn(
            "px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all",
            topLane === "jobs"
              ? "bg-primary/15 text-primary ring-1 ring-primary/30"
              : "text-muted-foreground hover:bg-muted/40",
          )}
        >
          <ClipboardList className="h-3.5 w-3.5 inline mr-1.5" />
          Jobs ({counts.all})
        </button>
        <span className="text-[10px] text-muted-foreground ml-2">
          {topLane === "packets"
            ? "Vendor-ready order packets — confirm, place, and receive."
            : "Job-grouped vendor breakdown (legacy PO view)."}
        </span>
      </div>

      {topLane === "packets" ? (
        <OrderingPacketsLane
          serverPagingEnabled={serverPagingEnabled}
          refreshToken={orderingRefreshToken}
          serverVendors={orderingSummary.data?.packetVendors ?? []}
        />
      ) : (
        <div className="cockpit-surface space-y-4">

      {/* Status pipeline — hairline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-y border-border/60 divide-x divide-border/60">
        {[
          { key: "not-ordered" as const, label: "Not Ordered", count: counts.notOrdered, icon: ClipboardList, tone: "muted" },
          { key: "ordered" as const, label: "Ordered", count: counts.ordered, icon: Package, tone: "info" },
          { key: "confirmed" as const, label: "Confirmed", count: counts.confirmed, icon: Clock, tone: "warning" },
          { key: "delivered" as const, label: "Delivered", count: counts.delivered, icon: CheckCircle2, tone: "success" },
        ].map((tile) => {
          const isActive = filter === tile.key;
          const toneCls = tile.tone === "info" ? "text-info" : tile.tone === "warning" ? "text-warning" : tile.tone === "success" ? "text-success" : "text-muted-foreground/70";
          return (
            <button
              key={tile.key}
              onClick={() => setFilter(isActive ? "all" : tile.key)}
              className={`px-4 py-3 text-left transition-colors ${isActive ? "bg-primary/[0.06]" : "hover:bg-muted/30"}`}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <tile.icon className={`h-3 w-3 ${toneCls}`} />
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">{tile.label}</span>
              </div>
              <p className="text-lg font-semibold tabular-nums">{tile.count}</p>
            </button>
          );
        })}
      </div>

      {/* Action Required */}
      {actionItems.length > 0 && (
        <ActionRequiredPanel
          title="Needs Action"
          items={actionItems}
          maxItems={5}
          emptyState={{ label: "Nothing to order right now" }}
        />
      )}

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search jobs…" />}
        right={
          <div className="flex items-center gap-2">
            <Tabs value={filter} onValueChange={v => setFilter(v as QueueFilter)} className="hidden md:block">
              <TabsList className="h-8">
                <TabsTrigger value="all" className="text-xs">All ({counts.all})</TabsTrigger>
                <TabsTrigger value="not-ordered" className="text-xs">Pending ({counts.notOrdered})</TabsTrigger>
                <TabsTrigger value="ordered" className="text-xs">Ordered ({counts.ordered})</TabsTrigger>
                <TabsTrigger value="confirmed" className="text-xs">Confirmed ({counts.confirmed})</TabsTrigger>
                <TabsTrigger value="delivered" className="text-xs">Delivered ({counts.delivered})</TabsTrigger>
              </TabsList>
            </Tabs>
            {/* View toggle — Single Queue ⇄ Three Sections (user preference, persisted) */}
            <div className="flex items-center border rounded-md p-0.5">
              <Button
                size="sm"
                variant={viewMode === "single" ? "secondary" : "ghost"}
                className="h-7 px-2 text-[11px] gap-1"
                onClick={() => setViewMode("single")}
                title="Single unified queue"
              >
                <LayoutList className="h-3 w-3" /> Single
              </Button>
              <Button
                size="sm"
                variant={viewMode === "sections" ? "secondary" : "ghost"}
                className="h-7 px-2 text-[11px] gap-1"
                onClick={() => setViewMode("sections")}
                title="Three sections — Ready to Prep / Prepared / Pushed"
              >
                <Rows3 className="h-3 w-3" /> Sections
              </Button>
            </div>
          </div>
        }
      />

      {/* Job List */}
      {filteredJobs.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={Package}
              title="No jobs ready to order"
              description="Jobs appear here once Build Review is approved and materials are confirmed."
              actionLabel="Open Build Review"
              onAction={() => navigate("/intake-review")}
            />
          </CardContent>
        </Card>
      ) : viewMode === "single" ? (
        <div
          ref={listViewportRef}
          className="overflow-y-auto rounded-md"
          style={{ maxHeight: LIST_VIEWPORT_HEIGHT }}
          onScroll={(event) => setListScrollTop(event.currentTarget.scrollTop)}
        >
          <>
            <div style={{ height: singleWindow.topSpacerHeight }} />
            <div className="space-y-2">
              {singleWindow.visibleItems.map(renderJobModalLauncher)}
            </div>
            <div style={{ height: singleWindow.bottomSpacerHeight }} />
          </>
        </div>
      ) : (
        <>
          <div className="space-y-6">
            {sectionConfig.map((sec) => {
              const items = partitioned[sec.key];
              const Icon = sec.icon;
              const isReadyToPrep = sec.key === "readyToPrep";
              return (
                <div key={sec.key}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <Icon className={`h-3.5 w-3.5 ${sec.tone}`} />
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {sec.label}
                    </h3>
                    <Badge variant="outline" className="text-[10px] tabular-nums">{items.length}</Badge>
                  </div>
                  {items.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="p-4 text-center text-[11px] text-muted-foreground">
                        Nothing in this lane right now.
                      </CardContent>
                    </Card>
                  ) : (
                    <SectionWindowedRows
                      items={items}
                      renderJobRow={isReadyToPrep ? renderJobModalLauncher : renderJobRow}
                      rowHeight={LIST_ROW_HEIGHT}
                      disableWindowing={!isReadyToPrep && Boolean(expandedJob)}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <Dialog
            open={Boolean(focusedJobModalEntry)}
            onOpenChange={(open) => {
              if (!open) setFocusedJobModalId(null);
            }}
          >
            <DialogContent className="max-w-6xl h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>
                  {focusedJobModalEntry ? `${focusedJobModalEntry.job.customerName} · ${getJobDisplayRef(focusedJobModalEntry.job)}` : "Job Details"}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  Review this job's vendor groups and manage purchase orders without expanding the row inline.
                </p>
              </DialogHeader>
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {!focusedJobModalEntry ? (
                  <Card className="border-dashed">
                    <CardContent className="p-6 text-center text-sm text-muted-foreground">
                      Select a job to open it here.
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {renderJobRow(focusedJobModalEntry, { forceExpanded: true })}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
      {standaloneOrders.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-1">
            Direct Orders
          </p>
          {standaloneOrders.map((order) => (
            <StandaloneOrderRow key={order.id} order={order} onReload={reloadStandaloneOrders} />
          ))}
        </div>
      )}

      <Dialog
        open={Boolean(markOrderedDialog)}
        onOpenChange={(open) => {
          if (!open) closeMarkOrderedDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Ordered</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">{markOrderedDialog?.vg.vendorName}</p>
              <p className="text-sm text-muted-foreground">
                {markOrderedDialog?.job.customerName}
              </p>
            </div>
            <UnmatchedSkuBanner skus={getUnmatchedSkus(markOrderedDialog?.vg.items as any, resolvedRegistryProducts)} />
            <div className="space-y-2">
              <Label htmlFor="mark-ordered-date">Actual order date</Label>
              <Input
                id="mark-ordered-date"
                type="date"
                value={markOrderedDate}
                onChange={(event) => setMarkOrderedDate(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This will be saved as the vendor order date for the purchase order.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Destination warehouse <span className="text-destructive">*</span></Label>
              <WarehouseSelector
                value={markOrderedWarehouseId}
                onChange={setMarkOrderedWarehouseId}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMarkOrderedDialog}>
              Cancel
            </Button>
            <PermissionGate permission="ordering_create">
              <Button
                onClick={() => { void handleConfirmMarkOrderedDialog(); }}
                disabled={!markOrderedDialog || !markOrderedWarehouseId || isPoActionInFlight(`order:${markOrderedDialog.job.id}:${markOrderedDialog.vg.vendorName}`) || getUnmatchedSkus(markOrderedDialog?.vg.items as any, resolvedRegistryProducts).length > 0}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {markOrderedDialog && isPoActionInFlight(`order:${markOrderedDialog.job.id}:${markOrderedDialog.vg.vendorName}`)
                  ? "Saving..."
                  : "Mark Ordered"}
              </Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(markDeliveredDialog)}
        onOpenChange={(open) => {
          if (!open) closeMarkDeliveredDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Delivered</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">{markDeliveredDialog?.vg.vendorName}</p>
              <p className="text-sm text-muted-foreground">
                {markDeliveredDialog?.job.customerName}
              </p>
            </div>
            <UnmatchedSkuBanner skus={getUnmatchedSkus(markDeliveredDialog?.job.materialsList, resolvedRegistryProducts)} />
            <div className="space-y-2">
              <Label htmlFor="mark-delivered-date">Actual delivered date</Label>
              <Input
                id="mark-delivered-date"
                type="date"
                value={markDeliveredDate}
                onChange={(event) => setMarkDeliveredDate(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This will be saved as the delivered date for the purchase order and receiving records.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMarkDeliveredDialog}>
              Cancel
            </Button>
            <PermissionGate permission="ordering_approve">
              <Button
                onClick={() => { void handleConfirmMarkDeliveredDialog(); }}
                disabled={!markDeliveredDialog || !markDeliveredDialog.vg.poId || isPoActionInFlight(`deliver:${markDeliveredDialog.vg.poId}`) || getUnmatchedSkus(markDeliveredDialog?.job.materialsList, resolvedRegistryProducts).length > 0}
              >
                <Truck className="mr-2 h-4 w-4" />
                {markDeliveredDialog?.vg.poId && isPoActionInFlight(`deliver:${markDeliveredDialog.vg.poId}`)
                  ? "Saving..."
                  : "Mark Delivered"}
              </Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reverse Order confirmation dialog */}
      <Dialog open={!!reverseOrderDialog} onOpenChange={(open) => { if (!open) setReverseOrderDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-destructive" />
              Reverse Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>This will pull back the purchase order and reset the job to <span className="font-medium">Not Ordered</span>:</p>
            <ul className="space-y-1 text-xs text-muted-foreground list-disc list-inside">
              <li>
                PO <span className="font-mono font-medium text-foreground">{reverseOrderDialog?.vg.poNumber ?? "—"}</span>
                {" "}— {reverseOrderDialog?.vg.vendorName}
              </li>
              <li>PO line items, shipment record, and receiving records will be deleted</li>
              {reverseOrderDialog?.vg.status === "Delivered" && (
                <li className="text-destructive font-medium">Warehouse allocations from delivery will also be removed</li>
              )}
            </ul>
            <p className="text-xs text-muted-foreground">
              The job will reappear in the ordering queue ready to be re-ordered with the correct materials.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseOrderDialog(null)}>Cancel</Button>
            <PermissionGate permission="ordering_approve">
              <Button
                variant="destructive"
                disabled={reverseOrderDialog?.vg.poId ? isPoActionInFlight(`reverse:${reverseOrderDialog.vg.poId}`) : false}
                onClick={() => {
                  if (reverseOrderDialog) void handleReverseOrder(reverseOrderDialog.job, reverseOrderDialog.vg);
                }}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {reverseOrderDialog?.vg.poId && isPoActionInFlight(`reverse:${reverseOrderDialog.vg.poId}`)
                  ? "Reversing..."
                  : "Reverse Order"}
              </Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PlaceOrderModal
        open={showPlaceOrderModal}
        onOpenChange={setShowPlaceOrderModal}
        registryProducts={resolvedRegistryProducts}
        vendorSkuMappings={orderingVendorSkuMappings}
        onSuccess={() => {
          void reloadStandaloneOrders();
          refreshOrderingServerPages();
        }}
      />
      {viewMode === "single" && <PaginationBar {...singlePagination} />}
        </div>
      )}
    </CockpitPage>
  );
}

function SectionWindowedRows({
  items,
  renderJobRow,
  rowHeight,
  disableWindowing = false,
}: {
  items: EnrichedOrderingJob[];
  renderJobRow: (item: EnrichedOrderingJob) => ReactNode;
  rowHeight: number;
  disableWindowing?: boolean;
}) {
  const viewportHeight = 420;
  const [scrollTop, setScrollTop] = useState(0);
  const windowed = useWindowedList(items, {
    itemHeight: rowHeight,
    viewportHeight,
    scrollTop,
    overscan: 6,
  });

  return (
    <div
      className="overflow-y-auto rounded-md"
      style={{ maxHeight: viewportHeight }}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      {disableWindowing ? (
        <div className="space-y-2">{items.map(renderJobRow)}</div>
      ) : (
        <>
          <div style={{ height: windowed.topSpacerHeight }} />
          <div className="space-y-2">{windowed.visibleItems.map(renderJobRow)}</div>
          <div style={{ height: windowed.bottomSpacerHeight }} />
        </>
      )}
    </div>
  );
}
